// src/components/user/ResumeBuilder.js

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/client';
import { useNavigate } from 'react-router-dom';
import PremiumSelect from '../admin/component/PremiumSelect';
import PageHeader, { PageCard, PageHeaderButton, PageShell } from '../common/PageHeader';
import {
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  AutoAwesome as SparklesIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  DesktopMac as DesktopIcon,
  TabletMac as TabletIcon,
  PhoneIphone as PhoneIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  SaveAlt as SaveIcon,
  GetApp as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const DEMO_RESUME_DATA = {
  personalInfo: {
    fullName: "Alexander Wright",
    title: "Senior Full Stack Engineer",
    email: "alexander.wright@email.com",
    phone: "+1 (555) 382-9921",
    location: "San Francisco, CA",
    linkedin: "https://linkedin.com/in/alexanderwright",
    github: "https://github.com/alexwright",
    website: "https://alexwright.dev",
    summary: "Dynamic and results-driven Senior Software Engineer with 8+ years of experience designing, building, and deploying robust full-stack applications. Proven track record in optimizing application performance, scaling cloud infrastructure, and leading cross-functional teams to deliver enterprise-grade software solutions using React, Node.js, and AWS."
  },
  education: [
    {
      school: "Stanford University",
      degree: "Master of Science in Computer Science",
      fieldOfStudy: "Software Engineering",
      startYear: "2016",
      endYear: "2018",
      grade: "3.92 GPA",
      location: "Stanford, CA",
      description: "Specialized in Distributed Systems and Artificial Intelligence."
    },
    {
      school: "University of California, Berkeley",
      degree: "Bachelor of Science in Computer Science",
      fieldOfStudy: "Computer Science",
      startYear: "2012",
      endYear: "2016",
      grade: "3.85 GPA",
      location: "Berkeley, CA",
      description: "Graduated with Honors. Active member of Computer Science Undergraduate Association."
    }
  ],
  experience: [
    {
      jobTitle: "Lead Full Stack Developer",
      company: "InnovateTech Solutions",
      location: "San Francisco, CA",
      startDate: "2021-06",
      endDate: "",
      isCurrent: true,
      description: "- Designed and spearheaded the transition from a legacy monolith to a microservices architecture, improving system scalability and reducing API response latency by 45%.\n- Led an agile engineering team of 8 to build and deploy a real-time analytics dashboard, generating over $2.5M in incremental revenue.\n- Implemented CI/CD pipelines using GitHub Actions and Docker, reducing deployment cycle times by 60%."
    },
    {
      jobTitle: "Senior Software Engineer",
      company: "CloudScale Systems",
      location: "Seattle, WA",
      startDate: "2018-09",
      endDate: "2021-05",
      isCurrent: false,
      description: "- Engineered highly responsive web interfaces using React, Redux, and Tailwind CSS, increasing user engagement metrics by 35%.\n- Optimized backend databases (PostgreSQL, Redis), reducing query execution times by 30% and eliminating system bottlenecks during peak traffic.\n- Mentored 5 junior developers, conducting code reviews and hosting weekly workshops."
    }
  ],
  projects: [
    {
      name: "DevSphere - Developer Platform",
      role: "Creator & Lead Architect",
      link: "https://github.com/alexwright/devsphere",
      technologies: "Next.js, Node.js, GraphQL, PostgreSQL, Docker",
      description: "- Created an open-source collaborative workspace for software development teams with real-time markdown editing and code compilation.\n- Scaled to support over 15,000 active developers globally with 99.9% uptime using AWS ECS."
    },
    {
      name: "SaaS Analytics Engine",
      role: "Backend Architect",
      link: "https://github.com/alexwright/saas-analytics",
      technologies: "Python, FastAPI, Apache Kafka, Redis, MongoDB",
      description: "- Architected a high-throughput event processing pipeline handling 50M+ daily telemetry events.\n- Built a real-time anomaly detection system powered by Redis TimeSeries and statistical models."
    }
  ],
  skills: [
    "JavaScript/TypeScript", "React/Next.js", "Node.js/Express", "Python/FastAPI",
    "PostgreSQL/MongoDB", "Docker/Kubernetes", "AWS (S3, Lambda, EC2)",
    "GraphQL/REST APIs", "CI/CD Pipelines", "System Architecture", "Git/Agile"
  ],
  certifications: [
    {
      name: "AWS Certified Solutions Architect – Professional",
      organization: "Amazon Web Services",
      date: "2024",
      credentialId: "AWS-PSA-9921",
      credentialUrl: ""
    },
    {
      name: "Certified ScrumMaster (CSM)",
      organization: "Scrum Alliance",
      date: "2023",
      credentialId: "CSM-8827-02",
      credentialUrl: ""
    }
  ],
  achievements: [
    {
      title: "Outstanding Technical Leadership Award",
      date: "2023",
      description: "Awarded by InnovateTech for successfully leading the migration of cloud databases under tight deadlines with zero downtime."
    },
    {
      title: "1st Place - Silicon Valley Tech Hackathon",
      date: "2019",
      description: "Won first prize among 150 teams for developing an AI-driven accessibility checker for web applications."
    }
  ]
};

export default function ResumeBuilder() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // States
  const [activeStep, setActiveStep] = useState(1);
  const [resumeId, setResumeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('All changes saved'); // 'Saving...', 'All changes saved', 'Unsaved changes'
  const [deviceView, setDeviceView] = useState('desktop'); // 'desktop', 'tablet', 'mobile'
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('All');
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const [alertDialog, setAlertDialog] = useState({
    isOpen: false,
    title: 'AptiView Notification',
    message: '',
    type: 'info' // 'info', 'warning', 'error', 'success'
  });

  const showAlert = (message, title = 'AptiView Notification', type = 'info') => {
    setAlertDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  // AI states
  const [improvingField, setImprovingField] = useState(null);
  const [aiContext, setAiContext] = useState('');

  // Job matching states
  const [publicJobs, setPublicJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [jobMatchScore, setJobMatchScore] = useState(null);
  const [jobMatchLoading, setJobMatchLoading] = useState(false);

  // Resume form state
  const [resumeData, setResumeData] = useState({
    personalInfo: {
      fullName: '',
      title: '',
      email: '',
      phone: '',
      location: '',
      linkedin: '',
      github: '',
      website: '',
      summary: ''
    },
    education: [],
    experience: [],
    projects: [],
    skills: [],
    certifications: [],
    achievements: [],
    template: 'modern_professional',
    themeColor: '#4f46e5' // default indigo
  });

  useEffect(() => {
    if (isTemplateModalOpen) {
      setPreviewTemplateId(resumeData.template);
    }
  }, [isTemplateModalOpen, resumeData.template]);

  // State for list inputs (e.g. adding new experience/education/etc)
  const [eduInput, setEduInput] = useState({ school: '', degree: '', fieldOfStudy: '', startYear: '', endYear: '', grade: '', location: '', description: '' });
  const [expInput, setExpInput] = useState({ jobTitle: '', company: '', location: '', startDate: '', endDate: '', isCurrent: false, description: '' });
  const [projInput, setProjInput] = useState({ name: '', role: '', link: '', technologies: '', description: '' });
  const [certInput, setCertInput] = useState({ name: '', organization: '', date: '', credentialId: '', credentialUrl: '' });
  const [awardInput, setAwardInput] = useState({ title: '', date: '', description: '' });
  const [skillInput, setSkillInput] = useState('');

  // Editing indices
  const [editingIndex, setEditingIndex] = useState({ type: '', index: null });

  // Prefill default user details from localStorage on mount
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user && !resumeId) {
        setResumeData(prev => ({
          ...prev,
          personalInfo: {
            ...prev.personalInfo,
            fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            email: user.email || ''
          }
        }));
      }
    } catch (e) {
      console.error(e);
    }
  }, [resumeId]);

  // Load existing resumes on mount to resume editing if active
  useEffect(() => {
    const loadActiveResume = async () => {
      setLoading(true);
      try {
        const response = await api.get('resumes/');
        if (response.data && Array.isArray(response.data.results)) {
          const active = response.data.results.find(r => r.is_active) || response.data.results[0];
          if (active && active.parsed_data) {
            setResumeId(active.id);
            const parsed = active.parsed_data || {};
            setResumeData({
              personalInfo: {
                fullName: parsed.personalInfo?.fullName || parsed.fullName || '',
                title: parsed.personalInfo?.title || parsed.title || '',
                email: parsed.personalInfo?.email || parsed.email || '',
                phone: parsed.personalInfo?.phone || parsed.phone || '',
                location: parsed.personalInfo?.location || parsed.location || '',
                linkedin: parsed.personalInfo?.linkedin || parsed.linkedin || '',
                github: parsed.personalInfo?.github || parsed.github || '',
                website: parsed.personalInfo?.website || parsed.website || '',
                summary: parsed.personalInfo?.summary || parsed.summary || ''
              },
              education: Array.isArray(parsed.education) ? parsed.education : [],
              experience: Array.isArray(parsed.experience) ? parsed.experience : [],
              projects: Array.isArray(parsed.projects) ? parsed.projects : [],
              skills: Array.isArray(parsed.skills) ? parsed.skills : (Array.isArray(parsed.extracted_skills) ? parsed.extracted_skills : []),
              certifications: Array.isArray(parsed.certifications) ? parsed.certifications : [],
              achievements: Array.isArray(parsed.achievements) ? parsed.achievements : [],
              template: parsed.template || 'modern_professional',
              themeColor: parsed.themeColor || '#4f46e5'
            });
          }
        }

        // Fetch public job descriptions for matching
        const jobsResponse = await api.get('public-jobs/');
        if (jobsResponse.data && Array.isArray(jobsResponse.data)) {
          setPublicJobs(jobsResponse.data);
        } else if (jobsResponse.data && Array.isArray(jobsResponse.data.results)) {
          setPublicJobs(jobsResponse.data.results);
        }
      } catch (err) {
        console.error("Failed to load active resume or jobs:", err);
      } finally {
        setLoading(false);
      }
    };
    loadActiveResume();
  }, []);

  // Sync / Auto-save logic
  useEffect(() => {
    // Basic flag to avoid auto-saving on initial page load before fetch finishes
    if (loading) return;
    setSaveStatus('Unsaved changes');
  }, [resumeData, loading]);

  // Dynamic ATS Score Calculation
  const calculateAtsScore = () => {
    let score = 0;
    const personalInfo = resumeData.personalInfo || {};
    const education = resumeData.education || [];
    const experience = resumeData.experience || [];
    const projects = resumeData.projects || [];
    const skills = resumeData.skills || [];
    const certifications = resumeData.certifications || [];
    const achievements = resumeData.achievements || [];

    // Contact info checks
    if (personalInfo.fullName) score += 10;
    if (personalInfo.title) score += 5;
    if (personalInfo.email) score += 10;
    if (personalInfo.phone) score += 10;
    if (personalInfo.location) score += 10;
    if (personalInfo.summary && personalInfo.summary.length > 50) score += 15;

    // Section presence checks
    if (education.length > 0) score += 10;
    if (experience.length > 0) score += 15;
    if (projects.length > 0) score += 10;
    if (skills.length > 0) {
      score += Math.min(10, skills.length * 2); // Max 10%
    }
    if (certifications.length > 0) score += 3;
    if (achievements.length > 0) score += 2;

    return Math.min(100, score);
  };

  const atsScore = calculateAtsScore();

  // Helper to determine score color/grade
  const getAtsGrade = (score) => {
    if (score >= 80) return { label: t('resumeBuilder.grades.veryGood', 'Very Good'), color: 'text-emerald-500', stroke: '#10b981' };
    if (score >= 60) return { label: t('resumeBuilder.grades.goodMatch', 'Good Match'), color: 'text-sky-500', stroke: '#0ea5e9' };
    if (score >= 40) return { label: t('resumeBuilder.grades.needsImprovement', 'Needs Improvement'), color: 'text-amber-500', stroke: '#f59e0b' };
    return { label: t('resumeBuilder.grades.poor', 'Poor'), color: 'text-rose-500', stroke: '#ef4444' };
  };

  const getStepTranslationKey = (num) => {
    const keys = {
      1: 'personalInfo',
      2: 'education',
      3: 'experience',
      4: 'projects',
      5: 'skills',
      6: 'certifications',
      7: 'achievements',
      8: 'aiReview'
    };
    return `resumeBuilder.sidebar.steps.${keys[num]}`;
  };

  const getSaveStatusLabel = (status) => {
    switch (status) {
      case 'All changes saved': return t('resumeBuilder.saveStatus.saved', 'All changes saved');
      case 'Unsaved changes': return t('resumeBuilder.saveStatus.unsaved', 'Unsaved changes');
      case 'Saving...': return t('resumeBuilder.saveStatus.saving', 'Saving...');
      case 'Failed to save': return t('resumeBuilder.saveStatus.failed', 'Failed to save');
      default: return status;
    }
  };

  const atsGrade = getAtsGrade(atsScore);

  // Resume Health calculation helper
  const calculateResumeHealth = () => {
    let count = 0;
    if (resumeData.personalInfo.fullName) count++;
    if (resumeData.personalInfo.title) count++;
    if (resumeData.personalInfo.email) count++;
    if (resumeData.personalInfo.phone) count++;
    if (resumeData.personalInfo.location) count++;
    if (resumeData.personalInfo.summary) count++;
    if (resumeData.education.length > 0) count++;
    if (resumeData.experience.length > 0) count++;
    if (resumeData.projects.length > 0) count++;
    if (resumeData.skills.length > 0) count++;
    if (resumeData.certifications.length > 0) count++;
    if (resumeData.achievements.length > 0) count++;
    
    return Math.min(100, Math.round((count / 12) * 100));
  };
  const resumeHealth = calculateResumeHealth();

  /*
  const getCompletedStepsCount = () => {
    let count = 0;
    if (resumeData.personalInfo.fullName && resumeData.personalInfo.email) count++;
    if (resumeData.education.length > 0) count++;
    if (resumeData.experience.length > 0) count++;
    if (resumeData.projects.length > 0) count++;
    if (resumeData.skills.length > 0) count++;
    if (resumeData.certifications.length > 0) count++;
    if (resumeData.achievements.length > 0) count++;
    count++; // Step 8 is template selection, always completed
    return count;
  };
  // const completedSteps = getCompletedStepsCount();
  */

  const isStepCompleted = (stepNum) => {
    if (stepNum === 1) return !!(resumeData.personalInfo.fullName && resumeData.personalInfo.email);
    if (stepNum === 2) return resumeData.education.length > 0;
    if (stepNum === 3) return resumeData.experience.length > 0;
    if (stepNum === 4) return resumeData.projects.length > 0;
    if (stepNum === 5) return resumeData.skills.length > 0;
    if (stepNum === 6) return resumeData.certifications.length > 0;
    if (stepNum === 7) return resumeData.achievements.length > 0;
    if (stepNum === 8) return true;
    return false;
  };

  const getSkillStrength = (index) => {
    const levels = [95, 90, 85, 80, 75];
    return levels[index % levels.length];
  };

  const renderActiveTemplate = (isForModal = false, customTplId = null, customData = null) => {
    const tpl = customTplId || resumeData.template;
    const outerData = resumeData;
    return ((resumeData) => {
    
    if (tpl === 'modern_professional') {
      return (
        <div className="flex h-full min-h-[297mm] text-left">
          {/* Sidebar */}
          <div className="w-[34%] bg-[#0f172a] text-[#f8fafc] p-4 flex flex-col gap-5">
            <div>
              <div className="text-sm font-bold uppercase tracking-wider text-white truncate">{resumeData.personalInfo.fullName || 'YOUR NAME'}</div>
              <div className="text-[10px] font-bold uppercase tracking-widest truncate" style={{ color: resumeData.themeColor }}>{resumeData.personalInfo.title || 'Professional Title'}</div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-[10px] font-bold text-white uppercase tracking-wider border-b border-slate-700 pb-1">Contact</div>
              {resumeData.personalInfo.location && <div className="text-[9px] text-slate-300 flex items-center gap-1">📍 <span className="truncate">{resumeData.personalInfo.location}</span></div>}
              {resumeData.personalInfo.phone && <div className="text-[9px] text-slate-300 flex items-center gap-1">📞 <span className="truncate">{resumeData.personalInfo.phone}</span></div>}
              {resumeData.personalInfo.email && <div className="text-[9px] text-slate-300 flex items-center gap-1 truncate">✉️ <span className="truncate">{resumeData.personalInfo.email}</span></div>}
              {resumeData.personalInfo.linkedin && <div className="text-[9px] text-slate-300 flex items-center gap-1 truncate">🔗 <span className="truncate">{resumeData.personalInfo.linkedin.replace(/https?:\/\//i, '')}</span></div>}
              {resumeData.personalInfo.github && <div className="text-[9px] text-slate-300 flex items-center gap-1 truncate">🐙 <span className="truncate">{resumeData.personalInfo.github.replace(/https?:\/\//i, '')}</span></div>}
              {resumeData.personalInfo.website && <div className="text-[9px] text-slate-300 flex items-center gap-1 truncate">🌐 <span className="truncate">{resumeData.personalInfo.website.replace(/https?:\/\//i, '')}</span></div>}
            </div>

            {resumeData.skills.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-[10px] font-bold text-white uppercase tracking-wider border-b border-slate-700 pb-1">Skills</div>
                <div className="flex flex-col gap-2">
                  {resumeData.skills.map((s, i) => {
                    const strength = getSkillStrength(i);
                    return (
                      <div key={i} className="text-[9px] text-slate-300">
                        <div className="flex justify-between mb-0.5">
                          <span className="font-semibold truncate mr-1">{s}</span>
                        </div>
                        <div className="h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${strength}%`, backgroundColor: resumeData.themeColor }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {resumeData.education.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-[10px] font-bold text-white uppercase tracking-wider border-b border-slate-700 pb-1">Education</div>
                {resumeData.education.map((edu, i) => (
                  <div key={i} className="text-[9px]">
                    <div className="font-bold text-white leading-tight">{edu.degree}</div>
                    <div className="text-slate-400 font-semibold">{edu.school}</div>
                    <div className="text-slate-500">{edu.startYear} - {edu.endYear} {edu.grade ? `| CGPA: ${edu.grade}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Main */}
          <div className="w-[66%] p-5 flex flex-col gap-4 bg-white">
            {resumeData.personalInfo.summary && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b-2 border-slate-100 pb-1 mb-1.5">Professional Summary</div>
                <p className="text-[10px] text-slate-600 leading-relaxed font-medium">{resumeData.personalInfo.summary}</p>
              </div>
            )}

            {resumeData.experience.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b-2 border-slate-100 pb-1 mb-2">Experience</div>
                <div className="space-y-3">
                  {resumeData.experience.map((exp, i) => (
                    <div key={i} className="text-[10px]">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>{exp.jobTitle}</span>
                        <span className="text-[8px] text-slate-400 font-semibold">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</span>
                      </div>
                      <div className="text-[9px] font-bold text-slate-500 mb-1">{exp.company} {exp.location ? `| ${exp.location}` : ''}</div>
                      {exp.description && (
                        <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5">
                          {exp.description.split('\n').filter(Boolean).map((bullet, idx) => (
                            <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resumeData.projects.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b-2 border-slate-100 pb-1 mb-2">Projects</div>
                <div className="space-y-3">
                  {resumeData.projects.map((proj, i) => (
                    <div key={i} className="text-[10px]">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>{proj.name} {proj.role ? `(${proj.role})` : ''}</span>
                        {proj.link && <span className="text-[8px] font-semibold" style={{ color: resumeData.themeColor }}>{proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</span>}
                      </div>
                      {proj.technologies && <div className="text-[8px] font-bold text-slate-400">Tech: {proj.technologies}</div>}
                      {proj.description && (
                        <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5 mt-1">
                          {proj.description.split('\n').filter(Boolean).map((bullet, idx) => (
                            <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resumeData.certifications.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b-2 border-slate-100 pb-1 mb-2">Certifications</div>
                <div className="space-y-2">
                  {resumeData.certifications.map((c, i) => (
                    <div key={i} className="text-[10px] flex justify-between">
                      <div>
                        <span className="font-bold text-slate-800">{c.name}</span>
                        <span className="text-slate-400 font-semibold"> - {c.organization}</span>
                      </div>
                      <span className="text-[8px] text-slate-400 font-semibold">{c.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resumeData.achievements.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b-2 border-slate-100 pb-1 mb-2">Achievements</div>
                <div className="space-y-2">
                  {resumeData.achievements.map((a, i) => (
                    <div key={i} className="text-[10px]">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>{a.title}</span>
                        <span className="text-[8px] text-slate-400 font-semibold">{a.date}</span>
                      </div>
                      {a.description && <p className="text-[9px] text-slate-600 mt-0.5 leading-normal">{a.description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (tpl === 'minimalist_clean') {
      return (
        <div className="p-6 bg-white h-full min-h-[297mm] text-left">
          <div className="text-center mb-4">
            <div className="text-lg font-bold text-slate-800 tracking-tight">{resumeData.personalInfo.fullName || 'YOUR NAME'}</div>
            <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: resumeData.themeColor }}>{resumeData.personalInfo.title || 'Professional Title'}</div>
            <div className="text-[8px] text-slate-500 mt-1 font-semibold flex justify-center gap-2 flex-wrap">
              <span>{resumeData.personalInfo.email}</span>
              {resumeData.personalInfo.phone && (
                <>
                  <span>|</span>
                  <span>{resumeData.personalInfo.phone}</span>
                </>
              )}
              {resumeData.personalInfo.location && (
                <>
                  <span>|</span>
                  <span>{resumeData.personalInfo.location}</span>
                </>
              )}
            </div>
          </div>

          {resumeData.personalInfo.summary && (
            <div className="mb-4">
              <div className="text-[9px] font-bold uppercase tracking-wider border-b border-slate-200 pb-0.5 mb-1.5" style={{ color: resumeData.themeColor }}>Summary</div>
              <p className="text-[9px] text-slate-600 leading-relaxed font-medium">{resumeData.personalInfo.summary}</p>
            </div>
          )}

          {resumeData.experience.length > 0 && (
            <div className="mb-4">
              <div className="text-[9px] font-bold uppercase tracking-wider border-b border-slate-200 pb-0.5 mb-1.5" style={{ color: resumeData.themeColor }}>Experience</div>
              {resumeData.experience.map((exp, i) => (
                <div key={i} className="mb-2 text-[9px]">
                  <div className="flex justify-between font-bold text-slate-800">
                    <span>{exp.jobTitle} at {exp.company}</span>
                    <span className="text-[8px] text-slate-400 font-semibold">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</span>
                  </div>
                  {exp.description && (
                    <ul className="list-disc pl-3 text-[8.5px] text-slate-600 mt-0.5 leading-normal">
                      {exp.description.split('\n').filter(Boolean).map((bullet, idx) => <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {resumeData.projects.length > 0 && (
            <div className="mb-4">
              <div className="text-[9px] font-bold uppercase tracking-wider border-b border-slate-200 pb-0.5 mb-1.5" style={{ color: resumeData.themeColor }}>Projects</div>
              {resumeData.projects.map((p, i) => (
                <div key={i} className="mb-2 text-[9px]">
                  <div className="flex justify-between font-bold text-slate-800">
                    <span>{p.name} {p.role ? `| ${p.role}` : ''}</span>
                    {p.link && <span className="text-[8px] font-semibold" style={{ color: resumeData.themeColor }}>{p.link}</span>}
                  </div>
                  {p.technologies && <div className="text-[8px] font-bold text-slate-400">Tech Stack: {p.technologies}</div>}
                  {p.description && (
                    <ul className="list-disc pl-3 text-[8.5px] text-slate-600 mt-0.5 leading-normal">
                      {p.description.split('\n').filter(Boolean).map((bullet, idx) => <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {resumeData.education.length > 0 && (
            <div className="mb-4">
              <div className="text-[9px] font-bold uppercase tracking-wider border-b border-slate-200 pb-0.5 mb-1.5" style={{ color: resumeData.themeColor }}>Education</div>
              {resumeData.education.map((edu, i) => (
                <div key={i} className="mb-2 text-[9px]">
                  <div className="flex justify-between font-bold text-slate-800">
                    <span>{edu.degree} {edu.fieldOfStudy ? `in ${edu.fieldOfStudy}` : ''}</span>
                    <span className="text-[8px] text-slate-400 font-semibold">{edu.startYear} - {edu.endYear}</span>
                  </div>
                  <div className="text-slate-500 font-semibold">{edu.school} {edu.grade ? `| Grade: ${edu.grade}` : ''}</div>
                </div>
              ))}
            </div>
          )}

          {resumeData.skills.length > 0 && (
            <div className="mb-4">
              <div className="text-[9px] font-bold uppercase tracking-wider border-b border-slate-200 pb-0.5 mb-1.5" style={{ color: resumeData.themeColor }}>Skills</div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {resumeData.skills.map((s, i) => (
                  <span key={i} className="border border-slate-300 text-[8px] text-slate-600 font-bold px-1.5 py-0.5 rounded">{s}</span>
                ))}
              </div>
            </div>
          )}

          {resumeData.certifications.length > 0 && (
            <div className="mb-4">
              <div className="text-[9px] font-bold uppercase tracking-wider border-b border-slate-200 pb-0.5 mb-1.5" style={{ color: resumeData.themeColor }}>Certifications</div>
              {resumeData.certifications.map((c, i) => (
                <div key={i} className="flex justify-between text-[9px] mb-1">
                  <div className="text-slate-700 font-semibold"><strong>{c.name}</strong> - {c.organization}</div>
                  <div className="text-[8px] text-slate-400 font-semibold">{c.date}</div>
                </div>
              ))}
            </div>
          )}

          {resumeData.achievements.length > 0 && (
            <div className="mb-4">
              <div className="text-[9px] font-bold uppercase tracking-wider border-b border-slate-200 pb-0.5 mb-1.5" style={{ color: resumeData.themeColor }}>Achievements</div>
              {resumeData.achievements.map((a, i) => (
                <div key={i} className="mb-2 text-[9px]">
                  <div className="flex justify-between font-bold text-slate-800">
                    <span>{a.title}</span>
                    <span className="text-[8px] text-slate-400 font-semibold">{a.date}</span>
                  </div>
                  {a.description && <p className="text-[8px] text-slate-600 mt-0.5 leading-normal">{a.description}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (tpl === 'creative_tech') {
      return (
        <div className="bg-white h-full min-h-[297mm] text-left">
          <div className="p-5 text-white" style={{ background: `linear-gradient(135deg, ${resumeData.themeColor} 0%, #1e1b4b 100%)` }}>
            <div className="text-lg font-bold">{resumeData.personalInfo.fullName || 'YOUR NAME'}</div>
            <div className="text-[10px] text-slate-200 uppercase font-bold tracking-widest">{resumeData.personalInfo.title || 'Professional Title'}</div>
            <div className="text-[8px] text-slate-300 mt-2 flex flex-wrap gap-3 font-semibold">
              <span>✉️ {resumeData.personalInfo.email}</span>
              {resumeData.personalInfo.phone && <span>📞 {resumeData.personalInfo.phone}</span>}
              {resumeData.personalInfo.location && <span>📍 {resumeData.personalInfo.location}</span>}
            </div>
          </div>

          <div className="p-4 flex gap-4 text-[9px]">
            <div className="w-[65%] flex flex-col gap-4">
              {resumeData.personalInfo.summary && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest border-b pb-0.5 mb-1" style={{ color: resumeData.themeColor, borderColor: `${resumeData.themeColor}22` }}>Profile</div>
                  <p className="text-slate-600 leading-relaxed font-medium">{resumeData.personalInfo.summary}</p>
                </div>
              )}

              {resumeData.experience.length > 0 && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest border-b pb-0.5 mb-1" style={{ color: resumeData.themeColor, borderColor: `${resumeData.themeColor}22` }}>Work Experience</div>
                  {resumeData.experience.map((exp, i) => (
                    <div key={i} className="mb-2">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>{exp.jobTitle}</span>
                        <span className="text-[8px] text-slate-400">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</span>
                      </div>
                      <div className="text-[8px] text-slate-500 font-bold">{exp.company} {exp.location ? `| ${exp.location}` : ''}</div>
                      {exp.description && (
                        <ul className="list-disc pl-3 text-slate-600 leading-normal mt-0.5">
                          {exp.description.split('\n').filter(Boolean).map((bullet, idx) => <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {resumeData.projects.length > 0 && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest border-b pb-0.5 mb-1" style={{ color: resumeData.themeColor, borderColor: `${resumeData.themeColor}22` }}>Key Projects</div>
                  {resumeData.projects.map((p, i) => (
                    <div key={i} className="mb-2">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>{p.name} {p.role ? `| ${p.role}` : ''}</span>
                        {p.link && <span className="text-[8px] font-semibold" style={{ color: resumeData.themeColor }}>{p.link.includes('github.com') ? 'GitHub' : 'Project Link'}</span>}
                      </div>
                      {p.technologies && <div className="text-[8px] font-bold text-slate-400">Tech: {p.technologies}</div>}
                      {p.description && (
                        <ul className="list-disc pl-3 text-slate-600 leading-normal mt-0.5">
                          {p.description.split('\n').filter(Boolean).map((bullet, idx) => <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="w-[35%] flex flex-col gap-4">
              {resumeData.skills.length > 0 && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest border-b pb-0.5 mb-1" style={{ color: resumeData.themeColor, borderColor: `${resumeData.themeColor}22` }}>Skills</div>
                  <div className="flex flex-wrap gap-1 mt-1 font-semibold">
                    {resumeData.skills.map((s, idx) => (
                      <span key={idx} className="text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${resumeData.themeColor}11`, color: resumeData.themeColor }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {resumeData.education.length > 0 && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest border-b pb-0.5 mb-1" style={{ color: resumeData.themeColor, borderColor: `${resumeData.themeColor}22` }}>Education</div>
                  {resumeData.education.map((edu, i) => (
                    <div key={i} className="mb-2 text-[9px]">
                      <div className="font-bold text-slate-800 leading-tight">{edu.degree} {edu.fieldOfStudy ? `in ${edu.fieldOfStudy}` : ''}</div>
                      <div className="text-slate-500 font-semibold">{edu.school}</div>
                      <div className="text-slate-400">{edu.startYear} - {edu.endYear} {edu.grade ? `| CGPA: ${edu.grade}` : ''}</div>
                    </div>
                  ))}
                </div>
              )}

              {resumeData.certifications.length > 0 && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest border-b pb-0.5 mb-1" style={{ color: resumeData.themeColor, borderColor: `${resumeData.themeColor}22` }}>Certificates</div>
                  {resumeData.certifications.map((c, i) => (
                    <div key={i} className="mb-2 text-[9px]">
                      <div className="font-bold text-slate-800 leading-tight">{c.name}</div>
                      <div className="text-slate-500 font-semibold">{c.organization}</div>
                      <div className="text-slate-400">{c.date}</div>
                    </div>
                  ))}
                </div>
              )}

              {resumeData.achievements.length > 0 && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest border-b pb-0.5 mb-1" style={{ color: resumeData.themeColor, borderColor: `${resumeData.themeColor}22` }}>Achievements</div>
                  {resumeData.achievements.map((a, i) => (
                    <div key={i} className="mb-2 text-[9px]">
                      <div className="font-bold text-slate-800 leading-tight">{a.title}</div>
                      <div className="text-slate-400">{a.date}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (tpl === 'executive_royal') {
      return (
        <div className="p-8 bg-white h-full min-h-[297mm] text-left flex flex-col gap-5">
          <div className="border-b-4 pb-4" style={{ borderColor: resumeData.themeColor }}>
            <div className="text-xl font-extrabold tracking-wide uppercase text-slate-900">{resumeData.personalInfo.fullName || 'YOUR NAME'}</div>
            <div className="text-xs font-bold uppercase tracking-widest mt-1" style={{ color: resumeData.themeColor }}>{resumeData.personalInfo.title || 'Professional Title'}</div>
            <div className="text-[10px] text-slate-500 mt-3 font-medium flex flex-wrap gap-x-4 gap-y-1">
              {resumeData.personalInfo.email && <span>✉️ {resumeData.personalInfo.email}</span>}
              {resumeData.personalInfo.phone && <span>📞 {resumeData.personalInfo.phone}</span>}
              {resumeData.personalInfo.location && <span>📍 {resumeData.personalInfo.location}</span>}
              {resumeData.personalInfo.linkedin && <span>🔗 {resumeData.personalInfo.linkedin.replace(/https?:\/\//i, '')}</span>}
              {resumeData.personalInfo.github && <span>🐙 {resumeData.personalInfo.github.replace(/https?:\/\//i, '')}</span>}
            </div>
          </div>

          {resumeData.personalInfo.summary && (
            <div>
              <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2">Professional Summary</div>
              <p className="text-[10px] text-slate-600 leading-relaxed font-medium">{resumeData.personalInfo.summary}</p>
            </div>
          )}

          {resumeData.experience.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2">Professional Experience</div>
              <div className="space-y-3">
                {resumeData.experience.map((exp, i) => (
                  <div key={i} className="text-[10px]">
                    <div className="flex justify-between font-bold text-slate-800">
                      <span>{exp.jobTitle}</span>
                      <span className="text-[8px] text-slate-400 font-semibold">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</span>
                    </div>
                    <div className="text-[9px] font-bold text-slate-500 mb-1">{exp.company} {exp.location ? `| ${exp.location}` : ''}</div>
                    {exp.description && (
                      <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5">
                        {exp.description.split('\n').filter(Boolean).map((bullet, idx) => (
                          <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {resumeData.projects.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2">Key Projects</div>
              <div className="space-y-3">
                {resumeData.projects.map((proj, i) => (
                  <div key={i} className="text-[10px]">
                    <div className="flex justify-between font-bold text-slate-800">
                      <span>{proj.name} {proj.role ? `(${proj.role})` : ''}</span>
                      {proj.link && <span className="text-[8px] font-semibold" style={{ color: resumeData.themeColor }}>Link</span>}
                    </div>
                    {proj.technologies && <div className="text-[8px] font-bold text-slate-400">Tech: {proj.technologies}</div>}
                    {proj.description && (
                      <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5 mt-1">
                        {proj.description.split('\n').filter(Boolean).map((bullet, idx) => (
                          <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {resumeData.education.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2">Education</div>
                <div className="space-y-2">
                  {resumeData.education.map((edu, i) => (
                    <div key={i} className="text-[9px]">
                      <div className="font-bold text-slate-800">{edu.degree}</div>
                      <div className="text-slate-500 font-semibold">{edu.school}</div>
                      <div className="text-slate-400">{edu.startYear} - {edu.endYear} {edu.grade ? `| CGPA: ${edu.grade}` : ''}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resumeData.skills.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2">Expertise & Skills</div>
                <div className="flex flex-wrap gap-1">
                  {resumeData.skills.map((s, idx) => (
                    <span key={idx} className="text-[8px] font-bold px-2 py-0.5 rounded border border-slate-200 text-slate-600 bg-slate-50">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {resumeData.certifications.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2">Certifications</div>
              <div className="space-y-1">
                {resumeData.certifications.map((c, i) => (
                  <div key={i} className="text-[9px] flex justify-between">
                    <div><strong>{c.name}</strong> - {c.organization}</div>
                    <div className="text-slate-400">{c.date}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {resumeData.achievements.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2">Achievements</div>
              <div className="space-y-1">
                {resumeData.achievements.map((a, i) => (
                  <div key={i} className="text-[9px] flex justify-between">
                    <div><strong>{a.title}</strong> - {a.description}</div>
                    <div className="text-slate-400">{a.date}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (tpl === 'academic_cv') {
      return (
        <div className="p-10 bg-white h-full min-h-[297mm] text-left flex flex-col gap-6" style={{ fontFamily: 'Georgia, serif' }}>
          <div className="text-center pb-4 border-b border-slate-200">
            <div className="text-2xl text-slate-900 font-bold uppercase tracking-wide">{resumeData.personalInfo.fullName || 'YOUR NAME'}</div>
            <div className="text-xs italic text-slate-600 mt-1">{resumeData.personalInfo.title || 'Curriculum Vitae'}</div>
            <div className="text-[9px] text-slate-500 mt-3 flex justify-center gap-x-3 gap-y-0.5 flex-wrap">
              {resumeData.personalInfo.email && <span>Email: {resumeData.personalInfo.email}</span>}
              {resumeData.personalInfo.phone && <span>• Phone: {resumeData.personalInfo.phone}</span>}
              {resumeData.personalInfo.location && <span>• Location: {resumeData.personalInfo.location}</span>}
              {resumeData.personalInfo.linkedin && <span>• LinkedIn: {resumeData.personalInfo.linkedin.replace(/https?:\/\//i, '')}</span>}
            </div>
          </div>

          {resumeData.personalInfo.summary && (
            <div>
              <div className="text-[11px] font-bold text-slate-900 uppercase tracking-widest mb-2 border-b border-slate-900/10 pb-0.5">Research Statement / Summary</div>
              <p className="text-[10px] text-slate-700 leading-relaxed font-medium italic">{resumeData.personalInfo.summary}</p>
            </div>
          )}

          {resumeData.experience.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-900 uppercase tracking-widest mb-2 border-b border-slate-900/10 pb-0.5">Professional Appointments</div>
              <div className="space-y-3">
                {resumeData.experience.map((exp, i) => (
                  <div key={i} className="flex gap-4 text-[10px]">
                    <div className="w-[20%] text-slate-500 font-semibold text-[9px] text-right">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</div>
                    <div className="w-[80%]">
                      <div className="font-bold text-slate-900">{exp.jobTitle}</div>
                      <div className="italic text-slate-600">{exp.company} {exp.location ? `, ${exp.location}` : ''}</div>
                      {exp.description && (
                        <ul className="list-disc pl-4 text-[9.5px] text-slate-700 space-y-0.5 mt-1">
                          {exp.description.split('\n').filter(Boolean).map((bullet, idx) => (
                            <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {resumeData.education.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-900 uppercase tracking-widest mb-2 border-b border-slate-900/10 pb-0.5">Education</div>
              <div className="space-y-3">
                {resumeData.education.map((edu, i) => (
                  <div key={i} className="flex gap-4 text-[10px]">
                    <div className="w-[20%] text-slate-500 font-semibold text-[9px] text-right">{edu.startYear} - {edu.endYear}</div>
                    <div className="w-[80%]">
                      <div className="font-bold text-slate-900">{edu.degree} {edu.fieldOfStudy ? `in ${edu.fieldOfStudy}` : ''}</div>
                      <div className="italic text-slate-600">{edu.school} {edu.grade ? `| Grade: ${edu.grade}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {resumeData.projects.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-900 uppercase tracking-widest mb-2 border-b border-slate-900/10 pb-0.5">Academic Projects</div>
              <div className="space-y-3">
                {resumeData.projects.map((proj, i) => (
                  <div key={i} className="flex gap-4 text-[10px]">
                    <div className="w-[20%] text-slate-500 font-semibold text-[9px] text-right">Project {i + 1}</div>
                    <div className="w-[80%]">
                      <div className="font-bold text-slate-900">{proj.name} {proj.role ? `(${proj.role})` : ''}</div>
                      {proj.technologies && <div className="text-[8.5px] italic text-slate-500">Tech: {proj.technologies}</div>}
                      {proj.description && (
                        <ul className="list-disc pl-4 text-[9.5px] text-slate-700 space-y-0.5 mt-1">
                          {proj.description.split('\n').filter(Boolean).map((bullet, idx) => (
                            <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {resumeData.skills.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-900 uppercase tracking-widest mb-2 border-b border-slate-900/10 pb-0.5">Methodology & Skills</div>
              <p className="text-[10px] text-slate-700 leading-relaxed font-medium pl-[20%]">
                {resumeData.skills.join(', ')}
              </p>
            </div>
          )}

          {resumeData.certifications.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-900 uppercase tracking-widest mb-2 border-b border-slate-900/10 pb-0.5">Certificates & Licenses</div>
              <div className="space-y-1 pl-[20%]">
                {resumeData.certifications.map((c, i) => (
                  <div key={i} className="text-[9.5px] text-slate-700">
                    <strong>{c.name}</strong> – {c.organization} ({c.date})
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (tpl === 'elegant_sidebar') {
      const initials = (resumeData.personalInfo.fullName || 'YN')
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
        
      return (
        <div className="flex h-full min-h-[297mm] text-left">
          {/* Sidebar */}
          <div className="w-[30%] bg-slate-50 border-r border-slate-200/60 p-4 flex flex-col gap-6">
            <div className="flex flex-col items-center gap-2 mt-4">
              <div className="h-12 w-12 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm" style={{ backgroundColor: resumeData.themeColor }}>
                {initials}
              </div>
              <div className="text-[11px] font-bold text-slate-800 text-center uppercase tracking-wider">{resumeData.personalInfo.fullName || 'YOUR NAME'}</div>
              <div className="text-[8px] font-semibold text-slate-400 text-center uppercase">{resumeData.personalInfo.title}</div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-200 pb-1">Contact Details</div>
              {resumeData.personalInfo.email && <div className="text-[8.5px] text-slate-600 truncate">✉️ {resumeData.personalInfo.email}</div>}
              {resumeData.personalInfo.phone && <div className="text-[8.5px] text-slate-600 truncate">📞 {resumeData.personalInfo.phone}</div>}
              {resumeData.personalInfo.location && <div className="text-[8.5px] text-slate-600 truncate">📍 {resumeData.personalInfo.location}</div>}
              {resumeData.personalInfo.linkedin && <div className="text-[8.5px] text-slate-600 truncate">🔗 {resumeData.personalInfo.linkedin.replace(/https?:\/\//i, '')}</div>}
            </div>

            {resumeData.skills.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-200 pb-1">Skills & Tools</div>
                <div className="flex flex-col gap-2">
                  {resumeData.skills.map((s, i) => {
                    const strength = getSkillStrength(i);
                    return (
                      <div key={i} className="text-[8.5px] text-slate-600">
                        <div className="mb-0.5 font-medium">{s}</div>
                        <div className="h-1 w-full bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${strength}%`, backgroundColor: resumeData.themeColor }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {resumeData.education.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-200 pb-1">Education</div>
                {resumeData.education.map((edu, i) => (
                  <div key={i} className="text-[8.5px] text-slate-600">
                    <div className="font-bold text-slate-700 leading-tight">{edu.degree}</div>
                    <div className="text-slate-500">{edu.school}</div>
                    <div className="text-slate-400">{edu.startYear} - {edu.endYear}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Main Column */}
          <div className="w-[70%] p-6 flex flex-col gap-5 bg-white">
            {resumeData.personalInfo.summary && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1 mb-1.5" style={{ color: resumeData.themeColor }}>About Me</div>
                <p className="text-[9.5px] text-slate-600 leading-relaxed font-medium">{resumeData.personalInfo.summary}</p>
              </div>
            )}

            {resumeData.experience.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1 mb-2" style={{ color: resumeData.themeColor }}>Work Experience</div>
                <div className="space-y-3">
                  {resumeData.experience.map((exp, i) => (
                    <div key={i} className="text-[9.5px]">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>{exp.jobTitle}</span>
                        <span className="text-[8px] text-slate-400 font-semibold">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</span>
                      </div>
                      <div className="text-[8.5px] font-bold text-slate-500 mb-1">{exp.company} {exp.location ? `| ${exp.location}` : ''}</div>
                      {exp.description && (
                        <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5">
                          {exp.description.split('\n').filter(Boolean).map((bullet, idx) => (
                            <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resumeData.projects.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1 mb-2" style={{ color: resumeData.themeColor }}>Projects Portfolio</div>
                <div className="space-y-3">
                  {resumeData.projects.map((proj, i) => (
                    <div key={i} className="text-[9.5px]">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>{proj.name} {proj.role ? `(${proj.role})` : ''}</span>
                        {proj.link && <span className="text-[8px] font-semibold" style={{ color: resumeData.themeColor }}>Code</span>}
                      </div>
                      {proj.technologies && <div className="text-[8px] font-bold text-slate-400">Tech: {proj.technologies}</div>}
                      {proj.description && (
                        <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5 mt-0.5">
                          {proj.description.split('\n').filter(Boolean).map((bullet, idx) => (
                            <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resumeData.certifications.length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1 mb-2" style={{ color: resumeData.themeColor }}>Certifications</div>
                <div className="space-y-1">
                  {resumeData.certifications.map((c, i) => (
                    <div key={i} className="text-[9px] flex justify-between">
                      <div><strong>{c.name}</strong> - {c.organization}</div>
                      <div className="text-slate-400">{c.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (tpl === 'bold_minimal') {
      return (
        <div className="bg-white h-full min-h-[297mm] text-left flex flex-col gap-6">
          {/* Bold Header */}
          <div className="p-8 text-white flex flex-col justify-center items-center text-center shadow-sm" style={{ backgroundColor: resumeData.themeColor }}>
            <div className="text-2xl font-black uppercase tracking-widest">{resumeData.personalInfo.fullName || 'YOUR NAME'}</div>
            <div className="text-xs font-bold tracking-widest uppercase opacity-90 mt-1">{resumeData.personalInfo.title || 'Professional Title'}</div>
          </div>
          
          <div className="bg-slate-900 text-slate-300 py-2 px-8 flex justify-center gap-6 flex-wrap text-[9px] font-bold tracking-wide">
            {resumeData.personalInfo.email && <span>✉️ {resumeData.personalInfo.email}</span>}
            {resumeData.personalInfo.phone && <span>📞 {resumeData.personalInfo.phone}</span>}
            {resumeData.personalInfo.location && <span>📍 {resumeData.personalInfo.location}</span>}
            {resumeData.personalInfo.linkedin && <span>🔗 {resumeData.personalInfo.linkedin.replace(/https?:\/\//i, '')}</span>}
          </div>

          <div className="px-8 pb-8 flex flex-col gap-5">
            {resumeData.personalInfo.summary && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-wider border-b-2 border-slate-200 pb-1 mb-2 text-slate-800">Professional Summary</div>
                <p className="text-[10px] text-slate-600 leading-relaxed font-medium">{resumeData.personalInfo.summary}</p>
              </div>
            )}

            {resumeData.experience.length > 0 && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-wider border-b-2 border-slate-200 pb-1 mb-2 text-slate-800">Experience</div>
                <div className="space-y-3">
                  {resumeData.experience.map((exp, i) => (
                    <div key={i} className="text-[9.5px]">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span className="text-slate-900 font-extrabold">{exp.jobTitle}</span>
                        <span className="text-[8px] text-slate-400 font-bold">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</span>
                      </div>
                      <div className="text-[8.5px] font-bold text-slate-500 mb-1">{exp.company} {exp.location ? `| ${exp.location}` : ''}</div>
                      {exp.description && (
                        <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5">
                          {exp.description.split('\n').filter(Boolean).map((bullet, idx) => (
                            <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resumeData.projects.length > 0 && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-wider border-b-2 border-slate-200 pb-1 mb-2 text-slate-800">Projects</div>
                <div className="space-y-3">
                  {resumeData.projects.map((proj, i) => (
                    <div key={i} className="text-[9.5px]">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span className="font-extrabold text-slate-900">{proj.name} {proj.role ? `(${proj.role})` : ''}</span>
                        {proj.link && <span className="text-[8px] font-semibold" style={{ color: resumeData.themeColor }}>{proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</span>}
                      </div>
                      {proj.technologies && <div className="text-[8px] font-bold text-slate-400">Tech Stack: {proj.technologies}</div>}
                      {proj.description && (
                        <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5 mt-1">
                          {proj.description.split('\n').filter(Boolean).map((bullet, idx) => (
                            <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {resumeData.education.length > 0 && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider border-b-2 border-slate-200 pb-1 mb-2 text-slate-800">Education</div>
                  <div className="space-y-2">
                    {resumeData.education.map((edu, i) => (
                      <div key={i} className="text-[9px]">
                        <div className="font-bold text-slate-800">{edu.degree}</div>
                        <div className="text-slate-500 font-semibold">{edu.school}</div>
                        <div className="text-slate-400">{edu.startYear} - {edu.endYear}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resumeData.skills.length > 0 && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider border-b-2 border-slate-200 pb-1 mb-2 text-slate-800">Skills</div>
                  <div className="flex flex-wrap gap-1">
                    {resumeData.skills.map((s, idx) => (
                      <span key={idx} className="text-[8px] font-bold px-2 py-0.5 rounded border border-slate-350 text-slate-700 bg-slate-50">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (tpl === 'hybrid_split') {
      return (
        <div className="p-6 bg-white h-full min-h-[297mm] text-left flex flex-col gap-5">
          <div className="border-b border-slate-200 pb-4 text-center">
            <div className="text-2xl font-bold uppercase tracking-tight text-slate-800">{resumeData.personalInfo.fullName || 'YOUR NAME'}</div>
            <div className="text-xs font-semibold uppercase tracking-wider mt-1" style={{ color: resumeData.themeColor }}>{resumeData.personalInfo.title || 'Professional Title'}</div>
            <div className="text-[9px] text-slate-500 mt-2 font-medium flex justify-center gap-x-4 gap-y-1">
              {resumeData.personalInfo.email && <span>✉️ {resumeData.personalInfo.email}</span>}
              {resumeData.personalInfo.phone && <span>📞 {resumeData.personalInfo.phone}</span>}
              {resumeData.personalInfo.location && <span>📍 {resumeData.personalInfo.location}</span>}
              {resumeData.personalInfo.linkedin && <span>🔗 {resumeData.personalInfo.linkedin.replace(/https?:\/\//i, '')}</span>}
            </div>
          </div>

          <div className="flex gap-6 flex-grow">
            {/* Left Column (55%) */}
            <div className="w-[55%] flex flex-col gap-4">
              {resumeData.personalInfo.summary && (
                <div>
                  <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-1.5" style={{ color: resumeData.themeColor }}>Executive Summary</div>
                  <p className="text-[9.5px] text-slate-600 leading-relaxed font-medium">{resumeData.personalInfo.summary}</p>
                </div>
              )}

              {resumeData.experience.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2" style={{ color: resumeData.themeColor }}>Experience</div>
                  <div className="space-y-3">
                    {resumeData.experience.map((exp, i) => (
                      <div key={i} className="text-[9.5px]">
                        <div className="flex justify-between font-bold text-slate-800">
                          <span>{exp.jobTitle}</span>
                          <span className="text-[8px] text-slate-400 font-semibold">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</span>
                        </div>
                        <div className="text-[8.5px] font-bold text-slate-500 mb-1">{exp.company}</div>
                        {exp.description && (
                          <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5">
                            {exp.description.split('\n').filter(Boolean).map((bullet, idx) => (
                              <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resumeData.projects.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2" style={{ color: resumeData.themeColor }}>Key Projects</div>
                  <div className="space-y-3">
                    {resumeData.projects.map((proj, i) => (
                      <div key={i} className="text-[9.5px]">
                        <div className="flex justify-between font-bold text-slate-800">
                          <span>{proj.name}</span>
                          {proj.link && <span className="text-[8px] font-semibold" style={{ color: resumeData.themeColor }}>{proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</span>}
                        </div>
                        {proj.technologies && <div className="text-[8px] font-bold text-slate-400">Tech: {proj.technologies}</div>}
                        {proj.description && (
                          <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5 mt-0.5">
                            {proj.description.split('\n').filter(Boolean).map((bullet, idx) => (
                              <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Vertical Divider */}
            <div className="w-[1px] bg-slate-200 self-stretch"></div>

            {/* Right Column (45%) */}
            <div className="w-[45%] flex flex-col gap-4">
              {resumeData.skills.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-1.5" style={{ color: resumeData.themeColor }}>Core Skills</div>
                  <div className="flex flex-wrap gap-1 mt-1 font-semibold">
                    {resumeData.skills.map((s, idx) => (
                      <span key={idx} className="text-[8px] font-bold px-1.5 py-0.5 rounded border border-slate-200 text-slate-700 bg-slate-50">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {resumeData.education.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-1.5" style={{ color: resumeData.themeColor }}>Education</div>
                  {resumeData.education.map((edu, i) => (
                    <div key={i} className="mb-2 text-[9px]">
                      <div className="font-bold text-slate-850 leading-tight">{edu.degree}</div>
                      <div className="text-slate-500">{edu.school}</div>
                      <div className="text-slate-400">{edu.startYear} - {edu.endYear}</div>
                    </div>
                  ))}
                </div>
              )}

              {resumeData.certifications.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-1.5" style={{ color: resumeData.themeColor }}>Certifications</div>
                  {resumeData.certifications.map((c, i) => (
                    <div key={i} className="mb-1 text-[9px] flex justify-between">
                      <div className="text-slate-700 font-semibold"><strong>{c.name}</strong> - {c.organization}</div>
                    </div>
                  ))}
                </div>
              )}

              {resumeData.achievements.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-1 mb-1.5" style={{ color: resumeData.themeColor }}>Achievements</div>
                  {resumeData.achievements.map((a, i) => (
                    <div key={i} className="mb-1 text-[9px] leading-normal">
                      <strong>{a.title}</strong> – {a.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // corporate_elite
    if (tpl === 'corporate_elite') {
      return (
        <div className="p-8 bg-white h-full min-h-[297mm] text-left flex flex-col gap-5" style={{ fontFamily: "'Inter', sans-serif" }}>
          {/* Top Divider with themeColor */}
          <div className="h-1 w-full flex-shrink-0" style={{ backgroundColor: resumeData.themeColor }}></div>
          
          {/* Header section */}
          <div className="text-center pb-2">
            <h1 className="text-xl font-extrabold uppercase tracking-wide text-slate-900">{resumeData.personalInfo.fullName || 'YOUR NAME'}</h1>
            <p className="text-xs font-bold uppercase tracking-wider mt-1" style={{ color: resumeData.themeColor }}>{resumeData.personalInfo.title || 'Professional Title'}</p>
            
            <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 text-[9px] text-slate-500 font-semibold mt-2.5">
              {resumeData.personalInfo.email && <span>✉️ {resumeData.personalInfo.email}</span>}
              {resumeData.personalInfo.phone && <span>• 📞 {resumeData.personalInfo.phone}</span>}
              {resumeData.personalInfo.location && <span>• 📍 {resumeData.personalInfo.location}</span>}
              {resumeData.personalInfo.linkedin && <span>• 🔗 {resumeData.personalInfo.linkedin.replace(/https?:\/\//i, '')}</span>}
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Executive Summary */}
          {resumeData.personalInfo.summary && (
            <div>
              <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-wider">Executive Summary</h3>
              <p className="text-[9.5px] text-slate-650 leading-relaxed mt-1 font-medium">{resumeData.personalInfo.summary}</p>
            </div>
          )}

          {/* Work Experience */}
          {resumeData.experience.length > 0 && (
            <div>
              <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-wider">Professional Experience</h3>
              <div className="space-y-3 mt-1.5">
                {resumeData.experience.map((exp, i) => (
                  <div key={i} className="text-[9.5px]">
                    <div className="flex justify-between items-baseline font-bold text-slate-800">
                      <span>{exp.jobTitle} <span className="font-normal text-slate-400">|</span> <span className="text-slate-500 font-semibold">{exp.company}</span></span>
                      <span className="text-[8.5px] text-slate-400 font-bold">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</span>
                    </div>
                    {exp.description && (
                      <ul className="list-disc pl-3.5 text-[9px] text-slate-600 space-y-0.5 mt-1">
                        {exp.description.split('\n').filter(Boolean).map((bullet, idx) => (
                          <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Projects */}
          {resumeData.projects.length > 0 && (
            <div>
              <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-wider">Key Projects</h3>
              <div className="space-y-3 mt-1.5">
                {resumeData.projects.map((proj, i) => (
                  <div key={i} className="text-[9.5px]">
                    <div className="flex justify-between items-baseline font-bold text-slate-800">
                      <span>{proj.name} {proj.role && <span className="font-medium text-slate-500">({proj.role})</span>}</span>
                      {proj.link && <span className="text-[8px] font-bold" style={{ color: resumeData.themeColor }}>{proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</span>}
                    </div>
                    {proj.technologies && <div className="text-[8.5px] font-semibold text-slate-400 mt-0.5">Tech Stack: {proj.technologies}</div>}
                    {proj.description && (
                      <ul className="list-disc pl-3.5 text-[9px] text-slate-650 space-y-0.5 mt-0.5">
                        {proj.description.split('\n').filter(Boolean).map((bullet, idx) => (
                          <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grid Layout for secondary sections */}
          <div className="grid grid-cols-2 gap-5 pt-1">
            {resumeData.education.length > 0 && (
              <div>
                <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1 mb-1.5">Education</h3>
                <div className="space-y-2">
                  {resumeData.education.map((edu, i) => (
                    <div key={i} className="text-[9px]">
                      <div className="font-bold text-slate-800">{edu.degree}</div>
                      <div className="text-slate-500 font-semibold">{edu.school}</div>
                      <div className="text-slate-400">{edu.startYear} - {edu.endYear}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resumeData.skills.length > 0 && (
              <div>
                <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1 mb-1.5">Core Skills</h3>
                <div className="flex flex-wrap gap-1 mt-1 font-semibold">
                  {resumeData.skills.map((s, idx) => (
                    <span key={idx} className="text-[8px] font-bold px-2 py-0.5 rounded border border-slate-200 text-slate-700 bg-slate-50">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // creative_marketing
    return (
      <div className="p-8 bg-white h-full min-h-[297mm] text-left flex flex-col gap-6">
        <div className="flex justify-between items-start border-b border-slate-100 pb-4">
          <div>
            <div className="text-2xl font-extrabold text-slate-900 tracking-tight">{resumeData.personalInfo.fullName || 'YOUR NAME'}</div>
            <div className="text-xs font-bold uppercase tracking-widest mt-1" style={{ color: resumeData.themeColor }}>{resumeData.personalInfo.title || 'Professional Title'}</div>
          </div>
          <div className="text-right text-[9px] text-slate-500 font-medium space-y-0.5">
            {resumeData.personalInfo.email && <div>✉️ {resumeData.personalInfo.email}</div>}
            {resumeData.personalInfo.phone && <div>📞 {resumeData.personalInfo.phone}</div>}
            {resumeData.personalInfo.location && <div>📍 {resumeData.personalInfo.location}</div>}
          </div>
        </div>

        {resumeData.personalInfo.summary && (
          <div className="p-3.5 rounded-xl border border-slate-200/50" style={{ backgroundColor: `${resumeData.themeColor}05` }}>
            <p className="text-[9.5px] text-slate-650 leading-relaxed font-semibold">{resumeData.personalInfo.summary}</p>
          </div>
        )}

        {resumeData.experience.length > 0 && (
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: resumeData.themeColor }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: resumeData.themeColor }}></span> Experience
            </div>
            <div className="space-y-4">
              {resumeData.experience.map((exp, i) => (
                <div key={i} className="text-[9.5px] border-l-2 pl-3" style={{ borderColor: `${resumeData.themeColor}33` }}>
                  <div className="flex justify-between font-bold text-slate-800">
                    <span className="font-extrabold text-slate-900">{exp.jobTitle}</span>
                    <span className="text-[8px] text-slate-400 font-bold">{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</span>
                  </div>
                  <div className="text-[8.5px] font-bold text-slate-500 mb-1">{exp.company} {exp.location ? `| ${exp.location}` : ''}</div>
                  {exp.description && (
                    <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5">
                      {exp.description.split('\n').filter(Boolean).map((bullet, idx) => (
                        <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {resumeData.projects.length > 0 && (
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: resumeData.themeColor }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: resumeData.themeColor }}></span> Key Projects
            </div>
            <div className="space-y-4">
              {resumeData.projects.map((proj, i) => (
                <div key={i} className="text-[9.5px] border-l-2 pl-3" style={{ borderColor: `${resumeData.themeColor}33` }}>
                  <div className="flex justify-between font-bold text-slate-800">
                    <span className="font-extrabold text-slate-900">{proj.name}</span>
                    {proj.link && <span className="text-[8px] font-semibold hover:underline" style={{ color: resumeData.themeColor }}>{proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</span>}
                  </div>
                  {proj.technologies && <div className="text-[8px] font-bold text-slate-400">Tech: {proj.technologies}</div>}
                  {proj.description && (
                    <ul className="list-disc pl-3 text-[9px] text-slate-600 space-y-0.5 mt-0.5">
                      {proj.description.split('\n').filter(Boolean).map((bullet, idx) => (
                        <li key={idx}>{bullet.replace(/^[-•]\s*/, '')}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          {resumeData.education.length > 0 && (
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: resumeData.themeColor }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: resumeData.themeColor }}></span> Education
              </div>
              <div className="space-y-2.5">
                {resumeData.education.map((edu, i) => (
                  <div key={i} className="text-[9px]">
                    <div className="font-bold text-slate-900 leading-tight">{edu.degree}</div>
                    <div className="text-slate-500 font-semibold">{edu.school}</div>
                    <div className="text-slate-400">{edu.startYear} - {edu.endYear}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {resumeData.skills.length > 0 && (
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: resumeData.themeColor }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: resumeData.themeColor }}></span> Skills
              </div>
              <div className="flex flex-wrap gap-1 font-semibold">
                {resumeData.skills.map((s, idx) => (
                  <span key={idx} className="text-[8px] font-extrabold px-2 py-1 rounded-full text-slate-850" style={{ backgroundColor: `${resumeData.themeColor}15`, color: resumeData.themeColor }}>{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
    })(customData || outerData);
  };

  // Generate HTML Resume String for backend Playwright conversion
  const getResumeHtml = () => {
    const personalInfo = resumeData.personalInfo || {};
    const education = resumeData.education || [];
    const experience = resumeData.experience || [];
    const projects = resumeData.projects || [];
    const skills = resumeData.skills || [];
    const certifications = resumeData.certifications || [];
    const achievements = resumeData.achievements || [];
    const template = resumeData.template || 'modern_professional';
    const themeColor = resumeData.themeColor || '#4f46e5';

    // Convert text summary and descriptions to simple paragraphs/bullets safely
    const formatDescription = (text) => {
      if (!text) return '';
      return text.split('\n').map(p => {
        if (p.trim().startsWith('-') || p.trim().startsWith('•')) {
          return `<li>${p.replace(/^[-•]\s*/, '')}</li>`;
        }
        return `<p style="margin: 0 0 6px 0; font-size: 13px; color: #475569; line-height: 1.5;">${p}</p>`;
      }).join('');
    };

    // Modern Professional Template
    if (template === 'modern_professional') {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Inter', sans-serif; margin: 0; padding: 0; color: #1e293b; background-color: #ffffff; }
            .container { display: flex; min-height: 297mm; }
            .sidebar { width: 32%; background-color: #0f172a; color: #f8fafc; padding: 25px 20px; box-sizing: border-box; }
            .main { width: 68%; padding: 30px 25px; box-sizing: border-box; }
            .name { font-size: 24px; font-weight: 800; text-transform: uppercase; margin: 0 0 5px 0; letter-spacing: 0.5px; }
            .title { font-size: 14px; font-weight: 600; color: ${themeColor}; text-transform: uppercase; margin: 0 0 20px 0; letter-spacing: 1px; }
            .contact-item { margin-bottom: 12px; font-size: 11px; color: #cbd5e1; word-break: break-all; }
            .contact-label { font-weight: 700; color: #ffffff; display: block; margin-bottom: 2px; text-transform: uppercase; font-size: 9px; letter-spacing: 0.5px; }
            .sidebar-section { margin-top: 30px; }
            .sidebar-title { font-size: 13px; font-weight: 700; color: #ffffff; border-bottom: 2px solid ${themeColor}; padding-bottom: 5px; margin: 0 0 15px 0; text-transform: uppercase; letter-spacing: 0.5px; }
            .sidebar-list { list-style: none; padding: 0; margin: 0; }
            .sidebar-list-item { font-size: 12px; margin-bottom: 8px; color: #e2e8f0; }
            .section-title { font-size: 14px; font-weight: 800; color: #0f172a; border-bottom: 2px solid #e2e8f0; padding-bottom: 5px; margin: 25px 0 15px 0; text-transform: uppercase; letter-spacing: 0.5px; }
            .section-title:first-of-type { margin-top: 0; }
            .item-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; }
            .item-title { font-size: 13px; font-weight: 700; color: #0f172a; }
            .item-sub { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 4px; }
            .item-date { font-size: 11px; color: #64748b; font-weight: 500; }
            .item-desc { font-size: 12px; color: #334155; margin-top: 6px; padding-left: 15px; margin-bottom: 15px; }
            .item-desc li { margin-bottom: 4px; line-height: 1.4; }
            .summary-text { font-size: 12.5px; color: #334155; line-height: 1.6; margin-bottom: 20px; }
            .pill-container { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
            .pill { background-color: #f1f5f9; color: #334155; font-size: 10px; font-weight: 600; padding: 4px 8px; border-radius: 4px; border: 1px solid #e2e8f0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="sidebar">
              <div class="name">${personalInfo.fullName || 'YOUR NAME'}</div>
              <div class="title">${personalInfo.title || 'Professional Title'}</div>
              
              <div class="sidebar-section">
                <div class="sidebar-title">Contact</div>
                ${personalInfo.location ? `<div class="contact-item"><span class="contact-label">Location</span>${personalInfo.location}</div>` : ''}
                ${personalInfo.phone ? `<div class="contact-item"><span class="contact-label">Phone</span>${personalInfo.phone}</div>` : ''}
                ${personalInfo.email ? `<div class="contact-item"><span class="contact-label">Email</span>${personalInfo.email}</div>` : ''}
                ${personalInfo.linkedin ? `<div class="contact-item"><span class="contact-label">LinkedIn</span>${personalInfo.linkedin}</div>` : ''}
                ${personalInfo.github ? `<div class="contact-item"><span class="contact-label">GitHub</span>${personalInfo.github}</div>` : ''}
                ${personalInfo.website ? `<div class="contact-item"><span class="contact-label">Portfolio</span>${personalInfo.website}</div>` : ''}
              </div>

              ${skills.length > 0 ? `
              <div class="sidebar-section">
                <div class="sidebar-title">Skills</div>
                <ul class="sidebar-list">
                  ${skills.map(s => `<li class="sidebar-list-item">${s}</li>`).join('')}
                </ul>
              </div>` : ''}

              ${education.length > 0 ? `
              <div class="sidebar-section">
                <div class="sidebar-title">Education</div>
                ${education.map(e => `
                  <div style="margin-bottom: 12px;">
                    <div style="font-size: 11px; font-weight: 700; color: #ffffff;">${e.degree} ${e.fieldOfStudy ? `in ${e.fieldOfStudy}` : ''}</div>
                    <div style="font-size: 10px; color: #cbd5e1; margin: 2px 0;">${e.school}</div>
                    <div style="font-size: 9px; color: #94a3b8;">${e.startYear} - ${e.endYear} ${e.grade ? `| Grade: ${e.grade}` : ''}</div>
                  </div>
                `).join('')}
              </div>` : ''}
            </div>

            <div class="main">
              ${personalInfo.summary ? `
              <div class="section-title">Professional Summary</div>
              <div class="summary-text">${personalInfo.summary}</div>` : ''}

              ${experience.length > 0 ? `
              <div class="section-title">Work Experience</div>
              ${experience.map(exp => `
                <div style="margin-bottom: 15px;">
                  <div class="item-header">
                    <div class="item-title">${exp.jobTitle}</div>
                    <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
                  </div>
                  <div class="item-sub">${exp.company} ${exp.location ? `| ${exp.location}` : ''}</div>
                  ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
                </div>
              `).join('')}
              ` : ''}

              ${projects.length > 0 ? `
              <div class="section-title">Projects</div>
              ${projects.map(p => `
                <div style="margin-bottom: 15px;">
                  <div class="item-header">
                    <div class="item-title">${p.name} ${p.role ? `(${p.role})` : ''}</div>
                    ${p.link ? `<div class="item-date"><a href="${p.link}" style="color: ${themeColor}; text-decoration: none;">${p.link.includes('github.com') ? 'GitHub' : 'Project Link'}</a></div>` : ''}
                  </div>
                  ${p.technologies ? `<div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 4px;">Tech Stack: ${p.technologies}</div>` : ''}
                  ${p.description ? `<ul class="item-desc">${formatDescription(p.description)}</ul>` : ''}
                </div>
              `).join('')}
              ` : ''}

              ${certifications.length > 0 ? `
              <div class="section-title">Certifications</div>
              ${certifications.map(c => `
                <div style="margin-bottom: 10px; display: flex; justify-content: space-between;">
                  <div>
                    <div style="font-size: 12px; font-weight: 700;">${c.name}</div>
                    <div style="font-size: 11px; color: #475569;">${c.organization} ${c.credentialId ? `| ID: ${c.credentialId}` : ''}</div>
                  </div>
                  <div style="font-size: 11px; color: #64748b;">${c.date}</div>
                </div>
              `).join('')}
              ` : ''}

              ${achievements.length > 0 ? `
              <div class="section-title">Achievements</div>
              ${achievements.map(a => `
                <div style="margin-bottom: 10px;">
                  <div style="font-size: 12px; font-weight: 700;">${a.title} ${a.date ? `(${a.date})` : ''}</div>
                  <div style="font-size: 11px; color: #475569; margin-top: 2px;">${a.description}</div>
                </div>
              `).join('')}
              ` : ''}
            </div>
          </div>
        </body>
        </html>
      `;
    }

    // Minimalist Clean Template
    if (template === 'minimalist_clean') {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Inter', sans-serif; margin: 0; padding: 40px; color: #334155; background-color: #ffffff; line-height: 1.5; }
            .header { text-align: center; margin-bottom: 25px; }
            .name { font-size: 28px; font-weight: 700; color: #0f172a; margin: 0 0 5px 0; }
            .title { font-size: 14px; font-weight: 600; color: ${themeColor}; text-transform: uppercase; margin: 0 0 10px 0; letter-spacing: 1px; }
            .contacts { font-size: 11px; color: #64748b; margin-top: 5px; }
            .contacts span { margin: 0 5px; }
            .divider { border-top: 1px solid #cbd5e1; margin: 20px 0; }
            .section-title { font-size: 13px; font-weight: 700; color: ${themeColor}; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; margin: 20px 0 10px 0; }
            .item { margin-bottom: 12px; }
            .item-header { display: flex; justify-content: space-between; align-items: flex-start; }
            .item-title { font-size: 12px; font-weight: 700; color: #0f172a; }
            .item-date { font-size: 11px; color: #64748b; }
            .item-sub { font-size: 11.5px; font-weight: 500; color: #475569; }
            .item-desc { font-size: 11.5px; color: #475569; margin: 4px 0 0 0; padding-left: 15px; }
            .pill-container { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
            .pill { border: 1px solid #cbd5e1; color: #475569; font-size: 10px; padding: 3px 6px; border-radius: 3px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="name">${personalInfo.fullName || 'YOUR NAME'}</div>
            <div class="title">${personalInfo.title || 'Professional Title'}</div>
            <div class="contacts">
              ${personalInfo.email || ''} <span>•</span> 
              ${personalInfo.phone || ''} <span>•</span> 
              ${personalInfo.location || ''}
              ${personalInfo.linkedin ? `<span>•</span> ${personalInfo.linkedin}` : ''}
              ${personalInfo.github ? `<span>•</span> ${personalInfo.github}` : ''}
            </div>
          </div>

          ${personalInfo.summary ? `
          <div class="section-title">Summary</div>
          <div style="font-size: 12px; color: #475569; line-height: 1.6;">${personalInfo.summary}</div>` : ''}

          ${experience.length > 0 ? `
          <div class="section-title">Experience</div>
          ${experience.map(exp => `
            <div class="item">
              <div class="item-header">
                <div class="item-title">${exp.jobTitle} at ${exp.company}</div>
                <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
              </div>
              ${exp.location ? `<div class="item-sub">${exp.location}</div>` : ''}
              ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
            </div>
          `).join('')}
          ` : ''}

          ${projects.length > 0 ? `
          <div class="section-title">Projects</div>
          ${projects.map(p => `
            <div class="item">
              <div class="item-header">
                <div class="item-title">${p.name} ${p.role ? `| ${p.role}` : ''}</div>
                <div class="item-date">${p.link ? p.link : ''}</div>
              </div>
              ${p.technologies ? `<div style="font-size: 11px; font-weight: 600; color: #64748b; margin-top: 2px;">Tech Stack: ${p.technologies}</div>` : ''}
              ${p.description ? `<ul class="item-desc">${formatDescription(p.description)}</ul>` : ''}
            </div>
          `).join('')}
          ` : ''}

          ${education.length > 0 ? `
          <div class="section-title">Education</div>
          ${education.map(e => `
            <div class="item">
              <div class="item-header">
                <div class="item-title">${e.degree} ${e.fieldOfStudy ? `in ${e.fieldOfStudy}` : ''}</div>
                <div class="item-date">${e.startYear} - ${e.endYear}</div>
              </div>
              <div class="item-sub">${e.school} ${e.grade ? `| Grade: ${e.grade}` : ''}</div>
            </div>
          `).join('')}
          ` : ''}

          ${skills.length > 0 ? `
          <div class="section-title">Skills</div>
          <div class="pill-container">
            ${skills.map(s => `<span class="pill">${s}</span>`).join('')}
          </div>` : ''}

          ${certifications.length > 0 ? `
          <div class="section-title">Certifications</div>
          ${certifications.map(c => `
            <div style="font-size: 11.5px; margin-bottom: 6px; display: flex; justify-content: space-between;">
              <div><strong>${c.name}</strong> - ${c.organization}</div>
              <div>${c.date}</div>
            </div>
          `).join('')}
          ` : ''}
        </body>
        </html>
      `;
    }

    // Creative Tech Template (Top banner gradient)
    if (template === 'creative_tech') {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Inter', sans-serif; margin: 0; padding: 0; color: #334155; background-color: #ffffff; }
            .banner { background: linear-gradient(135deg, ${themeColor} 0%, #1e1b4b 100%); color: #ffffff; padding: 40px 30px; }
            .name { font-size: 32px; font-weight: 800; margin: 0 0 5px 0; letter-spacing: 0.5px; }
            .title { font-size: 16px; font-weight: 500; color: #cbd5e1; margin: 0 0 15px 0; }
            .contacts { display: flex; flex-wrap: wrap; gap: 15px; font-size: 11px; color: #e2e8f0; }
            .body-content { padding: 30px; display: flex; gap: 30px; }
            .main-col { width: 65%; }
            .sidebar-col { width: 35%; }
            .section-title { font-size: 13px; font-weight: 800; color: ${themeColor}; text-transform: uppercase; border-bottom: 2px solid ${themeColor}22; padding-bottom: 5px; margin: 25px 0 12px 0; letter-spacing: 1px; }
            .section-title:first-of-type { margin-top: 0; }
            .item { margin-bottom: 15px; }
            .item-title { font-size: 13px; font-weight: 700; color: #1e293b; }
            .item-sub { font-size: 11.5px; color: #64748b; font-weight: 600; margin-top: 2px; }
            .item-date { font-size: 11px; color: #94a3b8; margin-bottom: 4px; }
            .item-desc { font-size: 11.5px; color: #475569; margin-top: 4px; padding-left: 15px; }
            .pill { background: ${themeColor}11; color: ${themeColor}; font-size: 10px; font-weight: 600; padding: 4px 8px; border-radius: 4px; display: inline-block; margin: 0 4px 6px 0; border: 1px solid ${themeColor}22; }
          </style>
        </head>
        <body>
          <div class="banner">
            <div class="name">${personalInfo.fullName || 'YOUR NAME'}</div>
            <div class="title">${personalInfo.title || 'Professional Title'}</div>
            <div class="contacts">
              <span>Location: ${personalInfo.location || ''}</span>
              <span>Phone: ${personalInfo.phone || ''}</span>
              <span>Email: ${personalInfo.email || ''}</span>
              ${personalInfo.linkedin ? `<span>LinkedIn: ${personalInfo.linkedin}</span>` : ''}
              ${personalInfo.github ? `<span>GitHub: ${personalInfo.github}</span>` : ''}
            </div>
          </div>

          <div class="body-content">
            <div class="main-col">
              ${personalInfo.summary ? `
              <div class="section-title">Profile</div>
              <div style="font-size: 12px; color: #475569; line-height: 1.6; margin-bottom: 20px;">${personalInfo.summary}</div>` : ''}

              ${experience.length > 0 ? `
              <div class="section-title">Work Experience</div>
              ${experience.map(exp => `
                <div class="item">
                  <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                    <div class="item-title">${exp.jobTitle}</div>
                    <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
                  </div>
                  <div class="item-sub">${exp.company} ${exp.location ? `| ${exp.location}` : ''}</div>
                  ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
                </div>
              `).join('')}
              ` : ''}

              ${projects.length > 0 ? `
              <div class="section-title">Key Projects</div>
              ${projects.map(p => `
                <div class="item">
                  <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                    <div class="item-title">${p.name}</div>
                    ${p.link ? `<div class="item-date"><a href="${p.link}" style="color: ${themeColor}; text-decoration: none;">${p.link.includes('github.com') ? 'GitHub' : 'Project Link'}</a></div>` : ''}
                  </div>
                  ${p.technologies ? `<div style="font-size: 11px; font-weight: 600; color: #64748b; margin-top: 2px;">Tech: ${p.technologies}</div>` : ''}
                  ${p.description ? `<ul class="item-desc">${formatDescription(p.description)}</ul>` : ''}
                </div>
              `).join('')}
              ` : ''}
            </div>

            <div class="sidebar-col">
              ${skills.length > 0 ? `
              <div class="section-title">Skills</div>
              <div style="margin-top: 10px;">
                ${skills.map(s => `<span class="pill">${s}</span>`).join('')}
              </div>` : ''}

              ${education.length > 0 ? `
              <div class="section-title">Education</div>
              ${education.map(e => `
                <div style="margin-bottom: 12px;">
                  <div style="font-size: 11.5px; font-weight: 700; color: #1e293b;">${e.degree}</div>
                  <div style="font-size: 11px; color: #475569; margin: 2px 0;">${e.school}</div>
                  <div style="font-size: 10px; color: #94a3b8;">${e.startYear} - ${e.endYear} ${e.grade ? `| CGPA: ${e.grade}` : ''}</div>
                </div>
              `).join('')}
              ` : ''}

              ${certifications.length > 0 ? `
              <div class="section-title">Certificates</div>
              ${certifications.map(c => `
                <div style="margin-bottom: 10px;">
                  <div style="font-size: 11.5px; font-weight: 700; color: #1e293b;">${c.name}</div>
                  <div style="font-size: 11px; color: #64748b; margin: 2px 0;">${c.organization}</div>
                  <div style="font-size: 10px; color: #94a3b8;">${c.date}</div>
                </div>
              `).join('')}
              ` : ''}
            </div>
          </div>
        </body>
        </html>
      `;
    }

    // Executive Royal Template
    if (template === 'executive_royal') {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Inter', sans-serif; margin: 0; padding: 40px; color: #1e293b; background-color: #ffffff; }
            .header-box { border-bottom: 4px solid ${themeColor}; padding-bottom: 15px; margin-bottom: 25px; }
            .name { font-size: 26px; font-weight: 800; text-transform: uppercase; color: #0f172a; margin: 0; }
            .title { font-size: 13px; font-weight: 700; color: ${themeColor}; text-transform: uppercase; margin: 5px 0 10px 0; letter-spacing: 1px; }
            .contacts { font-size: 11px; color: #475569; display: flex; flex-wrap: wrap; gap: 15px; }
            .section-title { font-size: 12.5px; font-weight: 700; color: #0f172a; text-transform: uppercase; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; margin: 25px 0 12px 0; letter-spacing: 0.5px; }
            .item { margin-bottom: 15px; }
            .item-header { display: flex; justify-content: space-between; align-items: flex-start; }
            .item-title { font-size: 12px; font-weight: 700; color: #0f172a; }
            .item-date { font-size: 11px; color: #64748b; font-weight: 600; }
            .item-sub { font-size: 11.5px; font-weight: 700; color: #475569; margin-bottom: 4px; }
            .item-desc { font-size: 11.5px; color: #334155; margin: 4px 0 0 0; padding-left: 15px; }
            .pill { display: inline-block; background-color: #f8fafc; border: 1px solid #e2e8f0; color: #475569; font-size: 10px; font-weight: 600; padding: 3px 6px; border-radius: 3px; margin: 0 4px 6px 0; }
            .grid-cols-2 { display: flex; gap: 20px; }
            .col { width: 50%; }
          </style>
        </head>
        <body>
          <div class="header-box">
            <div class="name">${personalInfo.fullName || 'YOUR NAME'}</div>
            <div class="title">${personalInfo.title || 'Professional Title'}</div>
            <div class="contacts">
              ${personalInfo.email ? `<span>✉️ ${personalInfo.email}</span>` : ''}
              ${personalInfo.phone ? `<span>📞 ${personalInfo.phone}</span>` : ''}
              ${personalInfo.location ? `<span>📍 ${personalInfo.location}</span>` : ''}
              ${personalInfo.linkedin ? `<span>🔗 ${personalInfo.linkedin}</span>` : ''}
            </div>
          </div>

          ${personalInfo.summary ? `
          <div>
            <div class="section-title">Professional Summary</div>
            <p style="font-size: 12px; color: #475569; line-height: 1.6; margin: 0 0 20px 0;">${personalInfo.summary}</p>
          </div>` : ''}

          ${experience.length > 0 ? `
          <div>
            <div class="section-title">Professional Experience</div>
            ${experience.map(exp => `
              <div class="item">
                <div class="item-header">
                  <div class="item-title">${exp.jobTitle}</div>
                  <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
                </div>
                <div class="item-sub">${exp.company} ${exp.location ? `| ${exp.location}` : ''}</div>
                ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
              </div>
            `).join('')}
          </div>` : ''}

          ${projects.length > 0 ? `
          <div>
            <div class="section-title">Key Projects</div>
            ${projects.map(proj => `
              <div class="item">
                <div class="item-header">
                  <div class="item-title">${proj.name} ${proj.role ? `(${proj.role})` : ''}</div>
                  ${proj.link ? `<div class="item-date"><a href="${proj.link}" style="color: ${themeColor}; text-decoration: none;">${proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</a></div>` : ''}
                </div>
                ${proj.technologies ? `<div style="font-size: 11px; font-weight: 600; color: #64748b; margin-top: 2px;">Tech: ${proj.technologies}</div>` : ''}
                ${proj.description ? `<ul class="item-desc">${formatDescription(proj.description)}</ul>` : ''}
              </div>
            `).join('')}
          </div>` : ''}

          <div class="grid-cols-2">
            ${education.length > 0 ? `
            <div class="col">
              <div class="section-title">Education</div>
              ${education.map(edu => `
                <div style="margin-bottom: 12px;">
                  <div style="font-size: 11.5px; font-weight: 700; color: #1e293b;">${edu.degree}</div>
                  <div style="font-size: 11px; color: #475569; margin: 2px 0;">${edu.school}</div>
                  <div style="font-size: 10px; color: #94a3b8;">${edu.startYear} - ${edu.endYear} ${edu.grade ? `| CGPA: ${edu.grade}` : ''}</div>
                </div>
              `).join('')}
            </div>` : ''}

            ${skills.length > 0 ? `
            <div class="col">
              <div class="section-title">Expertise & Skills</div>
              <div style="margin-top: 8px;">
                ${skills.map(s => `<span class="pill">${s}</span>`).join('')}
              </div>
            </div>` : ''}
          </div>

          ${certifications.length > 0 ? `
          <div>
            <div class="section-title">Certifications</div>
            ${certifications.map(c => `
              <div style="font-size: 11.5px; margin-bottom: 6px; display: flex; justify-content: space-between;">
                <div><strong>${c.name}</strong> - ${c.organization}</div>
                <div style="color: #64748b;">${c.date}</div>
              </div>
            `).join('')}
          </div>` : ''}
        </body>
        </html>
      `;
    }

    // Academic CV Template (Serif)
    if (template === 'academic_cv') {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Georgia', serif; margin: 0; padding: 50px; color: #1e293b; background-color: #ffffff; line-height: 1.6; }
            .header { text-align: center; border-bottom: 1px solid #94a3b8; padding-bottom: 20px; margin-bottom: 30px; }
            .name { font-size: 28px; font-weight: 700; text-transform: uppercase; color: #0f172a; margin: 0; }
            .title { font-size: 13px; font-style: italic; color: #4b5563; margin-top: 5px; }
            .contacts { font-size: 11px; color: #4b5563; margin-top: 10px; text-align: center; }
            .contacts span { margin: 0 5px; }
            .section-title { font-size: 12.5px; font-weight: 750; color: #0f172a; text-transform: uppercase; border-bottom: 1px solid #1e293b; padding-bottom: 3px; margin: 30px 0 15px 0; letter-spacing: 1px; }
            .item { display: flex; margin-bottom: 15px; font-size: 12px; }
            .item-date { width: 22%; text-align: right; padding-right: 20px; color: #64748b; font-weight: 600; font-size: 11px; box-sizing: border-box; }
            .item-details { width: 78%; }
            .item-title { font-weight: 700; color: #0f172a; }
            .item-sub { font-style: italic; color: #4b5563; margin: 2px 0; }
            .item-desc { font-size: 11.5px; color: #334155; margin: 5px 0 0 0; padding-left: 15px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="name">${personalInfo.fullName || 'YOUR NAME'}</div>
            <div class="title">${personalInfo.title || 'Curriculum Vitae'}</div>
            <div class="contacts">
              ${personalInfo.email ? `<span>Email: ${personalInfo.email}</span>` : ''}
              ${personalInfo.phone ? `<span>• Phone: ${personalInfo.phone}</span>` : ''}
              ${personalInfo.location ? `<span>• Location: ${personalInfo.location}</span>` : ''}
            </div>
          </div>

          ${personalInfo.summary ? `
          <div>
            <div class="section-title">Research Statement / Summary</div>
            <p style="font-size: 12px; color: #334155; font-style: italic; line-height: 1.6; margin: 0 0 20px 0;">${personalInfo.summary}</p>
          </div>` : ''}

          ${experience.length > 0 ? `
          <div>
            <div class="section-title">Professional Appointments</div>
            ${experience.map(exp => `
              <div class="item">
                <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
                <div class="item-details">
                  <div class="item-title">${exp.jobTitle}</div>
                  <div class="item-sub">${exp.company} ${exp.location ? `, ${exp.location}` : ''}</div>
                  ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
                </div>
              </div>
            `).join('')}
          </div>` : ''}

          ${education.length > 0 ? `
          <div>
            <div class="section-title">Education</div>
            ${education.map(edu => `
              <div class="item">
                <div class="item-date">${edu.startYear} - ${edu.endYear}</div>
                <div class="item-details">
                  <div class="item-title">${edu.degree} ${edu.fieldOfStudy ? `in ${edu.fieldOfStudy}` : ''}</div>
                  <div class="item-sub">${edu.school} ${edu.grade ? `| Grade: ${edu.grade}` : ''}</div>
                </div>
              </div>
            `).join('')}
          </div>` : ''}

          ${projects.length > 0 ? `
          <div>
            <div class="section-title">Academic Projects</div>
            ${projects.map((proj, idx) => `
              <div class="item">
                <div class="item-date">Project ${idx + 1}</div>
                <div class="item-details">
                  <div class="item-title">${proj.name} ${proj.role ? `(${proj.role})` : ''}</div>
                  ${proj.technologies ? `<div style="font-size: 10.5px; font-style: italic; color: #4b5563;">Tech: ${proj.technologies}</div>` : ''}
                  ${proj.description ? `<ul class="item-desc">${formatDescription(proj.description)}</ul>` : ''}
                </div>
              </div>
            `).join('')}
          </div>` : ''}

          ${skills.length > 0 ? `
          <div>
            <div class="section-title">Methodology & Skills</div>
            <p style="font-size: 12px; color: #334155; margin-left: 22%;">${skills.join(', ')}</p>
          </div>` : ''}

          ${certifications.length > 0 ? `
          <div>
            <div class="section-title">Certificates & Licenses</div>
            <div style="margin-left: 22%;">
              ${certifications.map(c => `
                <div style="font-size: 11.5px; margin-bottom: 6px;">
                  <strong>${c.name}</strong> – ${c.organization} (${c.date})
                </div>
              `).join('')}
            </div>
          </div>` : ''}
        </body>
        </html>
      `;
    }

    // Elegant Sidebar Template
    if (template === 'elegant_sidebar') {
      const initials = (personalInfo.fullName || 'YN')
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Inter', sans-serif; margin: 0; padding: 0; color: #334155; background-color: #ffffff; }
            .container { display: flex; min-height: 297mm; }
            .sidebar { width: 30%; background-color: #f8fafc; border-right: 1px solid #e2e8f0; padding: 30px 20px; box-sizing: border-box; }
            .main { width: 70%; padding: 30px 25px; box-sizing: border-box; }
            .avatar { width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: 700; color: #ffffff; margin: 0 auto 15px auto; }
            .sidebar-name { font-size: 15px; font-weight: 800; text-transform: uppercase; text-align: center; color: #1e293b; margin: 0; }
            .sidebar-title { font-size: 11px; font-weight: 705; text-transform: uppercase; text-align: center; color: #94a3b8; margin: 4px 0 25px 0; }
            .sidebar-section { margin-bottom: 25px; }
            .sidebar-section-title { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; border-bottom: 1px solid #cbd5e1; padding-bottom: 3px; margin-bottom: 12px; }
            .sidebar-item { font-size: 11px; margin-bottom: 10px; color: #4b5563; word-break: break-all; }
            .section-title { font-size: 13px; font-weight: 800; color: ${themeColor}; text-transform: uppercase; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; margin: 25px 0 12px 0; }
            .section-title:first-of-type { margin-top: 0; }
            .item { margin-bottom: 15px; }
            .item-header { display: flex; justify-content: space-between; align-items: flex-start; }
            .item-title { font-size: 12px; font-weight: 700; color: #1e293b; }
            .item-date { font-size: 11px; color: #94a3b8; font-weight: 600; }
            .item-sub { font-size: 11.5px; color: #64748b; font-weight: 600; }
            .item-desc { font-size: 11.5px; color: #475569; margin: 4px 0 0 0; padding-left: 15px; }
            .skill-bar { height: 4px; background: #e2e8f0; border-radius: 2px; margin-top: 4px; width: 100%; overflow: hidden; }
            .skill-fill { height: 100%; border-radius: 2px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="sidebar">
              <div class="avatar" style="background-color: ${themeColor}">${initials}</div>
              <div class="sidebar-name">${personalInfo.fullName || 'YOUR NAME'}</div>
              <div class="sidebar-title">${personalInfo.title || 'Professional Title'}</div>

              <div class="sidebar-section">
                <div class="sidebar-section-title">Contact</div>
                ${personalInfo.email ? `<div class="sidebar-item">✉️ ${personalInfo.email}</div>` : ''}
                ${personalInfo.phone ? `<div class="sidebar-item">📞 ${personalInfo.phone}</div>` : ''}
                ${personalInfo.location ? `<div class="sidebar-item">📍 ${personalInfo.location}</div>` : ''}
                ${personalInfo.linkedin ? `<div class="sidebar-item">🔗 ${personalInfo.linkedin}</div>` : ''}
              </div>

              ${skills.length > 0 ? `
              <div class="sidebar-section">
                <div class="sidebar-section-title">Skills</div>
                ${skills.map((s, i) => {
                  const strength = getSkillStrength(i);
                  return `
                    <div class="sidebar-item" style="margin-bottom: 12px;">
                      <div style="font-weight: 600;">${s}</div>
                      <div class="skill-bar">
                        <div class="skill-fill" style="width: ${strength}%; background-color: ${themeColor}"></div>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>` : ''}

              ${education.length > 0 ? `
              <div class="sidebar-section">
                <div class="sidebar-section-title">Education</div>
                ${education.map(edu => `
                  <div class="sidebar-item" style="margin-bottom: 12px;">
                    <div style="font-weight: 700; color: #334155;">${edu.degree}</div>
                    <div style="color: #64748b; font-size: 10px;">${edu.school}</div>
                    <div style="color: #94a3b8; font-size: 9px;">${edu.startYear} - ${edu.endYear}</div>
                  </div>
                `).join('')}
              </div>` : ''}
            </div>

            <div class="main">
              ${personalInfo.summary ? `
              <div class="section-title">About Me</div>
              <p style="font-size: 12px; color: #475569; line-height: 1.6; margin-bottom: 20px;">${personalInfo.summary}</p>` : ''}

              ${experience.length > 0 ? `
              <div class="section-title">Work Experience</div>
              ${experience.map(exp => `
                <div class="item">
                  <div class="item-header">
                    <div class="item-title">${exp.jobTitle}</div>
                    <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
                  </div>
                  <div class="item-sub">${exp.company} ${exp.location ? `| ${exp.location}` : ''}</div>
                  ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
                </div>
              `).join('')}
              ` : ''}

              ${projects.length > 0 ? `
              <div class="section-title">Projects Portfolio</div>
              ${projects.map(proj => `
                <div class="item">
                  <div class="item-header">
                    <div class="item-title">${proj.name} ${proj.role ? `(${proj.role})` : ''}</div>
                    ${proj.link ? `<div class="item-date"><a href="${proj.link}" style="color: ${themeColor}; text-decoration: none;">${proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</a></div>` : ''}
                  </div>
                  ${proj.technologies ? `<div style="font-size: 11px; font-weight: 600; color: #64748b; margin-top: 2px;">Tech: ${proj.technologies}</div>` : ''}
                  ${proj.description ? `<ul class="item-desc">${formatDescription(proj.description)}</ul>` : ''}
                </div>
              `).join('')}
              ` : ''}

              ${certifications.length > 0 ? `
              <div class="section-title">Certifications</div>
              ${certifications.map(c => `
                <div style="font-size: 11.5px; margin-bottom: 6px; display: flex; justify-content: space-between;">
                  <div><strong>${c.name}</strong> - ${c.organization}</div>
                  <div style="color: #64748b;">${c.date}</div>
                </div>
              `).join('')}
              ` : ''}
            </div>
          </div>
        </body>
        </html>
      `;
    }

    // Bold Minimal Template
    if (template === 'bold_minimal') {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Inter', sans-serif; margin: 0; padding: 0; color: #334155; background-color: #ffffff; }
            .banner { background-color: ${themeColor}; color: #ffffff; padding: 45px 30px; text-align: center; }
            .name { font-size: 30px; font-weight: 900; text-transform: uppercase; letter-spacing: 1px; margin: 0; }
            .title { font-size: 14px; font-weight: 700; text-transform: uppercase; opacity: 0.9; margin: 5px 0 0 0; }
            .contacts-bar { background-color: #0f172a; color: #cbd5e1; padding: 8px 30px; text-align: center; font-size: 11px; font-weight: 700; }
            .contacts-bar span { margin: 0 10px; }
            .content { padding: 35px 40px; }
            .section-title { font-size: 13px; font-weight: 900; color: #1e293b; text-transform: uppercase; border-bottom: 2px solid #e2e8f0; padding-bottom: 4px; margin: 25px 0 12px 0; }
            .section-title:first-of-type { margin-top: 0; }
            .item { margin-bottom: 15px; }
            .item-header { display: flex; justify-content: space-between; align-items: flex-start; }
            .item-title { font-size: 12.5px; font-weight: 850; color: #0f172a; }
            .item-date { font-size: 11px; color: #64748b; font-weight: 750; }
            .item-sub { font-size: 11.5px; font-weight: 700; color: #475569; }
            .item-desc { font-size: 11.5px; color: #475569; margin: 4px 0 0 0; padding-left: 15px; }
            .pill { display: inline-block; background-color: #f8fafc; border: 1px solid #cbd5e1; color: #475569; font-size: 10px; font-weight: 700; padding: 3px 6px; border-radius: 3px; margin: 0 4px 6px 0; }
            .grid-cols-2 { display: flex; gap: 20px; }
            .col { width: 50%; }
          </style>
        </head>
        <body>
          <div class="banner">
            <div class="name">${personalInfo.fullName || 'YOUR NAME'}</div>
            <div class="title">${personalInfo.title || 'Professional Title'}</div>
          </div>
          
          <div class="contacts-bar">
            ${personalInfo.email ? `<span>✉️ ${personalInfo.email}</span>` : ''}
            ${personalInfo.phone ? `<span>📞 ${personalInfo.phone}</span>` : ''}
            ${personalInfo.location ? `<span>📍 ${personalInfo.location}</span>` : ''}
            ${personalInfo.linkedin ? `<span>🔗 ${personalInfo.linkedin}</span>` : ''}
          </div>

          <div class="content">
            ${personalInfo.summary ? `
            <div>
              <div class="section-title">Professional Summary</div>
              <p style="font-size: 12px; color: #475569; line-height: 1.6; margin: 0 0 20px 0;">${personalInfo.summary}</p>
            </div>` : ''}

            ${experience.length > 0 ? `
            <div>
              <div class="section-title">Experience</div>
              ${experience.map(exp => `
                <div class="item">
                  <div class="item-header">
                    <div class="item-title">${exp.jobTitle}</div>
                    <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
                  </div>
                  <div class="item-sub">${exp.company} ${exp.location ? `| ${exp.location}` : ''}</div>
                  ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
                </div>
              `).join('')}
            </div>` : ''}

            ${projects.length > 0 ? `
            <div>
              <div class="section-title">Projects</div>
              ${projects.map(proj => `
                <div class="item">
                  <div class="item-header">
                    <div class="item-title">${proj.name} ${proj.role ? `(${proj.role})` : ''}</div>
                    ${proj.link ? `<div class="item-date"><a href="${proj.link}" style="color: ${themeColor}; text-decoration: none;">${proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</a></div>` : ''}
                  </div>
                  ${proj.technologies ? `<div style="font-size: 11px; font-weight: 600; color: #64748b; margin-top: 2px;">Tech Stack: ${proj.technologies}</div>` : ''}
                  ${proj.description ? `<ul class="item-desc">${formatDescription(proj.description)}</ul>` : ''}
                </div>
              `).join('')}
            </div>` : ''}

            <div class="grid-cols-2">
              ${education.length > 0 ? `
              <div class="col">
                <div class="section-title">Education</div>
                ${education.map(edu => `
                  <div style="margin-bottom: 12px;">
                    <div style="font-size: 11.5px; font-weight: 700; color: #1e293b;">${edu.degree}</div>
                    <div style="font-size: 11px; color: #475569; margin: 2px 0;">${edu.school}</div>
                    <div style="font-size: 10px; color: #94a3b8;">${edu.startYear} - ${edu.endYear}</div>
                  </div>
                `).join('')}
              </div>` : ''}

              ${skills.length > 0 ? `
              <div class="col">
                <div class="section-title">Skills</div>
                <div style="margin-top: 8px;">
                  ${skills.map(s => `<span class="pill">${s}</span>`).join('')}
                </div>
              </div>` : ''}
            </div>
          </div>
        </body>
        </html>
      `;
    }

    // Hybrid Split Template
    if (template === 'hybrid_split') {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Inter', sans-serif; margin: 0; padding: 40px; color: #334155; background-color: #ffffff; }
            .header { text-align: center; border-bottom: 1px solid #e2e8f0; padding-bottom: 15px; margin-bottom: 25px; }
            .name { font-size: 26px; font-weight: 800; text-transform: uppercase; color: #1e293b; margin: 0; }
            .title { font-size: 13px; font-weight: 700; color: ${themeColor}; text-transform: uppercase; margin: 5px 0 10px 0; }
            .contacts { font-size: 11px; color: #64748b; }
            .contacts span { margin: 0 8px; }
            .columns-wrapper { display: flex; gap: 30px; }
            .left-col { width: 55%; }
            .right-col { width: 45%; border-left: 1px solid #e2e8f0; padding-left: 25px; }
            .section-title { font-size: 12.5px; font-weight: 700; color: ${themeColor}; text-transform: uppercase; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; margin: 25px 0 12px 0; }
            .section-title:first-of-type { margin-top: 0; }
            .item { margin-bottom: 15px; }
            .item-header { display: flex; justify-content: space-between; align-items: flex-start; }
            .item-title { font-size: 12px; font-weight: 700; color: #1e293b; }
            .item-date { font-size: 10.5px; color: #94a3b8; font-weight: 600; }
            .item-sub { font-size: 11px; font-weight: 600; color: #64748b; }
            .item-desc { font-size: 11.5px; color: #475569; margin: 4px 0 0 0; padding-left: 15px; }
            .pill { display: inline-block; background-color: #f8fafc; border: 1px solid #e2e8f0; color: #475569; font-size: 10px; font-weight: 600; padding: 3px 6px; border-radius: 3px; margin: 0 4px 6px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="name">${personalInfo.fullName || 'YOUR NAME'}</div>
            <div class="title">${personalInfo.title || 'Professional Title'}</div>
            <div class="contacts">
              ${personalInfo.email ? `<span>✉️ ${personalInfo.email}</span>` : ''}
              ${personalInfo.phone ? `<span>📞 ${personalInfo.phone}</span>` : ''}
              ${personalInfo.location ? `<span>📍 ${personalInfo.location}</span>` : ''}
            </div>
          </div>

          <div class="columns-wrapper">
            <div class="left-col">
              ${personalInfo.summary ? `
              <div>
                <div class="section-title">Executive Summary</div>
                <p style="font-size: 11.5px; color: #475569; line-height: 1.6; margin: 0 0 20px 0;">${personalInfo.summary}</p>
              </div>` : ''}

              ${experience.length > 0 ? `
              <div>
                <div class="section-title">Experience</div>
                ${experience.map(exp => `
                  <div class="item">
                    <div class="item-header">
                      <div class="item-title">${exp.jobTitle}</div>
                      <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
                    </div>
                    <div class="item-sub">${exp.company}</div>
                    ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
                  </div>
                `).join('')}
              </div>` : ''}

              ${projects.length > 0 ? `
              <div>
                <div class="section-title">Key Projects</div>
                ${projects.map(proj => `
                  <div class="item">
                    <div class="item-header">
                      <div class="item-title">${proj.name}</div>
                      ${proj.link ? `<div class="item-date"><a href="${proj.link}" style="color: ${themeColor}; text-decoration: none;">${proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</a></div>` : ''}
                    </div>
                    ${proj.technologies ? `<div style="font-size: 11px; font-weight: 600; color: #64748b; margin-top: 2px;">Tech: ${proj.technologies}</div>` : ''}
                    ${proj.description ? `<ul class="item-desc">${formatDescription(proj.description)}</ul>` : ''}
                  </div>
                `).join('')}
              </div>` : ''}
            </div>

            <div class="right-col">
              ${skills.length > 0 ? `
              <div>
                <div class="section-title">Core Skills</div>
                <div style="margin-top: 8px;">
                  ${skills.map(s => `<span class="pill">${s}</span>`).join('')}
                </div>
              </div>` : ''}

              ${education.length > 0 ? `
              <div>
                <div class="section-title">Education</div>
                ${education.map(edu => `
                  <div class="item">
                    <div class="item-title">${edu.degree}</div>
                    <div class="item-sub">${edu.school}</div>
                    <div class="item-date">${edu.startYear} - ${edu.endYear}</div>
                  </div>
                `).join('')}
              </div>` : ''}

              ${certifications.length > 0 ? `
              <div>
                <div class="section-title">Certifications</div>
                ${certifications.map(c => `
                  <div style="font-size: 11px; margin-bottom: 6px;">
                    <strong>${c.name}</strong> - ${c.organization}
                  </div>
                `).join('')}
              </div>` : ''}

              ${achievements.length > 0 ? `
              <div>
                <div class="section-title">Achievements</div>
                ${achievements.map(a => `
                  <div style="font-size: 11px; margin-bottom: 8px; line-height: 1.4;">
                    <strong>${a.title}</strong> – ${a.description}
                  </div>
                `).join('')}
              </div>` : ''}
            </div>
          </div>
        </body>
        </html>
      `;
    }

    // Corporate Elite Template
    if (template === 'corporate_elite') {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Inter', sans-serif; margin: 0; padding: 40px; color: #1e293b; background-color: #ffffff; }
            .top-bar { height: 4px; background-color: ${themeColor}; margin-bottom: 25px; }
            .header { text-align: center; margin-bottom: 25px; }
            .name { font-size: 24px; font-weight: 850; text-transform: uppercase; color: #0f172a; margin: 0; letter-spacing: 0.5px; }
            .title { font-size: 13px; font-weight: 700; color: ${themeColor}; text-transform: uppercase; margin: 5px 0 10px 0; letter-spacing: 0.5px; }
            .contacts { font-size: 10.5px; color: #64748b; }
            .contacts span { margin: 0 6px; }
            .section-title { font-size: 12px; font-weight: 700; color: #0f172a; text-transform: uppercase; border-bottom: 1.5px solid #cbd5e1; padding-bottom: 3px; margin: 25px 0 12px 0; letter-spacing: 0.5px; }
            .item { margin-bottom: 15px; }
            .item-header { display: flex; justify-content: space-between; align-items: flex-start; }
            .item-title { font-size: 12.5px; font-weight: 700; color: #0f172a; }
            .item-title span { font-weight: 500; color: #64748b; }
            .item-date { font-size: 11px; color: #94a3b8; font-weight: 700; }
            .item-desc { font-size: 11.5px; color: #475569; margin: 4px 0 0 0; padding-left: 15px; }
            .grid-cols-2 { display: flex; gap: 30px; }
            .col { width: 50%; }
            .pill { display: inline-block; background-color: #f8fafc; border: 1px solid #e2e8f0; color: #475569; font-size: 10.5px; font-weight: 700; padding: 3px 6px; border-radius: 3px; margin: 0 4px 6px 0; }
          </style>
        </head>
        <body>
          <div class="top-bar"></div>
          <div class="header">
            <div class="name">${personalInfo.fullName || 'YOUR NAME'}</div>
            <div class="title">${personalInfo.title || 'Professional Title'}</div>
            <div class="contacts">
              ${personalInfo.email ? `<span>✉️ ${personalInfo.email}</span>` : ''}
              ${personalInfo.phone ? `<span>• 📞 ${personalInfo.phone}</span>` : ''}
              ${personalInfo.location ? `<span>• 📍 ${personalInfo.location}</span>` : ''}
              ${personalInfo.linkedin ? `<span>• 🔗 ${personalInfo.linkedin}</span>` : ''}
            </div>
          </div>

          ${personalInfo.summary ? `
          <div>
            <div class="section-title">Executive Summary</div>
            <p style="font-size: 11.5px; color: #475569; line-height: 1.6; margin: 0 0 20px 0;">${personalInfo.summary}</p>
          </div>` : ''}

          ${experience.length > 0 ? `
          <div>
            <div class="section-title">Professional Experience</div>
            ${experience.map(exp => `
              <div class="item">
                <div class="item-header">
                  <div class="item-title">${exp.jobTitle} <span>| ${exp.company}</span></div>
                  <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
                </div>
                ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
              </div>
            `).join('')}
          </div>` : ''}

          ${projects.length > 0 ? `
          <div>
            <div class="section-title">Key Projects</div>
            ${projects.map(proj => `
              <div class="item">
                <div class="item-header">
                  <div class="item-title">${proj.name} ${proj.role ? `<span>(${proj.role})</span>` : ''}</div>
                  ${proj.link ? `<div class="item-date"><a href="${proj.link}" style="color: ${themeColor}; text-decoration: none;">${proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</a></div>` : ''}
                </div>
                ${proj.technologies ? `<div style="font-size: 11px; font-weight: 600; color: #64748b; margin-top: 2px;">Tech Stack: ${proj.technologies}</div>` : ''}
                ${proj.description ? `<ul class="item-desc">${formatDescription(proj.description)}</ul>` : ''}
              </div>
            `).join('')}
          </div>` : ''}

          <div class="grid-cols-2">
            ${education.length > 0 ? `
            <div class="col">
              <div class="section-title">Education</div>
              ${education.map(edu => `
                <div style="margin-bottom: 12px;">
                  <div style="font-size: 11.5px; font-weight: 700; color: #1e293b;">${edu.degree}</div>
                  <div style="font-size: 11px; color: #475569; margin: 2px 0;">${edu.school}</div>
                  <div style="font-size: 10px; color: #94a3b8;">${edu.startYear} - ${edu.endYear}</div>
                </div>
              `).join('')}
            </div>` : ''}

            ${skills.length > 0 ? `
            <div class="col">
              <div class="section-title">Core Skills</div>
              <div style="margin-top: 8px;">
                ${skills.map(s => `<span class="pill">${s}</span>`).join('')}
              </div>
            </div>` : ''}
          </div>
        </body>
        </html>
      `;
    }

    // Creative Marketing Template (Pastel/Bubble)
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: 'Inter', sans-serif; margin: 0; padding: 40px; color: #334155; background-color: #ffffff; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #e2e8f0; padding-bottom: 20px; margin-bottom: 25px; }
          .name { font-size: 26px; font-weight: 800; color: #0f172a; margin: 0; }
          .title { font-size: 13px; font-weight: 700; color: ${themeColor}; text-transform: uppercase; margin-top: 4px; }
          .contacts { text-align: right; font-size: 11px; color: #475569; line-height: 1.4; }
          .summary-highlight { background-color: ${themeColor}0d; border: 1px solid ${themeColor}1a; border-radius: 10px; padding: 15px; margin-bottom: 25px; }
          .section-title { font-size: 12.5px; font-weight: 850; color: ${themeColor}; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; margin: 30px 0 15px 0; }
          .section-title::before { content: ""; display: inline-block; width: 6px; height: 6px; border-radius: 50%; background-color: ${themeColor}; margin-right: 8px; }
          .item { border-left: 2px solid ${themeColor}22; padding-left: 15px; margin-bottom: 20px; }
          .item-header { display: flex; justify-content: space-between; align-items: flex-start; }
          .item-title { font-size: 12.5px; font-weight: 800; color: #0f172a; }
          .item-date { font-size: 10.5px; color: #94a3b8; font-weight: 700; }
          .item-sub { font-size: 11px; font-weight: 700; color: #475569; margin: 2px 0; }
          .item-desc { font-size: 11.5px; color: #475569; margin: 4px 0 0 0; padding-left: 15px; }
          .pill { display: inline-block; background-color: ${themeColor}1a; color: ${themeColor}; font-size: 10px; font-weight: 700; padding: 4px 8px; border-radius: 12px; margin: 0 4px 6px 0; border: none; }
          .grid-cols-2 { display: flex; gap: 30px; }
          .col { width: 50%; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="name">${personalInfo.fullName || 'YOUR NAME'}</div>
            <div class="title">${personalInfo.title || 'Professional Title'}</div>
          </div>
          <div class="contacts">
            ${personalInfo.email ? `<div>✉️ ${personalInfo.email}</div>` : ''}
            ${personalInfo.phone ? `<div>📞 ${personalInfo.phone}</div>` : ''}
            ${personalInfo.location ? `<div>📍 ${personalInfo.location}</div>` : ''}
          </div>
        </div>

        ${personalInfo.summary ? `
        <div class="summary-highlight">
          <p style="font-size: 12px; color: #334155; line-height: 1.6; font-weight: 600; margin: 0;">${personalInfo.summary}</p>
        </div>` : ''}

        ${experience.length > 0 ? `
        <div>
          <div class="section-title">Experience</div>
          <div style="margin-top: 15px;">
            ${experience.map(exp => `
              <div class="item">
                <div class="item-header">
                  <div class="item-title">${exp.jobTitle}</div>
                  <div class="item-date">${exp.startDate} - ${exp.isCurrent ? 'Present' : exp.endDate}</div>
                </div>
                <div class="item-sub">${exp.company} ${exp.location ? `| ${exp.location}` : ''}</div>
                ${exp.description ? `<ul class="item-desc">${formatDescription(exp.description)}</ul>` : ''}
              </div>
            `).join('')}
          </div>
        </div>` : ''}

        ${projects.length > 0 ? `
        <div>
          <div class="section-title">Key Projects</div>
          <div style="margin-top: 15px;">
            ${projects.map(proj => `
              <div class="item">
                <div class="item-header">
                  <div class="item-title">${proj.name}</div>
                  ${proj.link ? `<div class="item-date"><a href="${proj.link}" style="color: ${themeColor}; text-decoration: none;">${proj.link.includes('github.com') ? 'GitHub' : 'Project Link'}</a></div>` : ''}
                </div>
                ${proj.technologies ? `<div style="font-size: 11px; font-weight: 600; color: #64748b; margin-top: 2px;">Tech: ${proj.technologies}</div>` : ''}
                ${proj.description ? `<ul class="item-desc">${formatDescription(proj.description)}</ul>` : ''}
              </div>
            `).join('')}
          </div>
        </div>` : ''}

        <div class="grid-cols-2">
          ${education.length > 0 ? `
          <div class="col">
            <div class="section-title">Education</div>
            <div style="margin-top: 15px; gap: 10px;">
              ${education.map(edu => `
                <div style="margin-bottom: 12px;">
                  <div style="font-size: 11.5px; font-weight: 700; color: #1e293b;">${edu.degree}</div>
                  <div style="font-size: 11px; color: #475569; margin: 2px 0;">${edu.school}</div>
                  <div style="font-size: 10px; color: #94a3b8;">${edu.startYear} - ${edu.endYear}</div>
                </div>
              `).join('')}
            </div>
          </div>` : ''}

          ${skills.length > 0 ? `
          <div class="col">
            <div class="section-title">Skills</div>
            <div style="margin-top: 15px;">
              ${skills.map(s => `<span class="pill">${s}</span>`).join('')}
            </div>
          </div>` : ''}
        </div>
      </body>
      </html>
    `;
  };

  // Backend Saves Built Resume Action
  const saveDraft = async () => {
    if (saving) return;
    setSaving(true);
    setSaveStatus('Saving...');
    try {
      const payload = {
        html_content: getResumeHtml(),
        parsed_data: resumeData,
        filename: `${resumeData.personalInfo.fullName || 'built'}_resume.pdf`,
      };
      if (resumeId) {
        payload.resume_id = resumeId;
      }

      const response = await api.post('resumes/save_built_resume/', payload);
      if (response.data && response.data.id) {
        setResumeId(response.data.id);
        setSaveStatus('All changes saved');
      } else {
        setSaveStatus('Failed to save');
      }
    } catch (err) {
      console.error(err);
      setSaveStatus('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Download PDF Action
  const downloadPDF = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      // Auto-save the latest changes first
      await saveDraft();

      const payload = {
        html_content: getResumeHtml(),
        filename: `${resumeData.personalInfo.fullName || 'my'}_resume.pdf`,
        compact_mode: true
      };

      const response = await api.post('generate-pdf/', payload, {
        responseType: 'blob'
      });

      // Create download link in browser
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${resumeData.personalInfo.fullName || 'my'}_resume.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
      showAlert(t('resumeBuilder.alerts.downloadError', 'Failed to download PDF. Please check connection and try again.'), t('resumeBuilder.alerts.downloadErrorTitle', 'Download Error'), "error");
    } finally {
      setDownloading(false);
    }
  };

  // AI Assistant Call
  const handleAiImprove = async (fieldName, currentValue) => {
    if (!currentValue) {
      showAlert(t('resumeBuilder.alerts.aiSuggestionWarning', 'Please enter some text first for AI to improve.'), t('resumeBuilder.alerts.aiSuggestionTitle', 'AI Suggestion'), "warning");
      return;
    }
    setImprovingField(fieldName);
    try {
      const response = await api.post('resumes/improve_field/', {
        field_name: fieldName,
        field_value: currentValue,
        context: aiContext || `Improve resume section ${fieldName}`
      });

      if (response.data && response.data.improved_text) {
        if (fieldName === 'summary') {
          setResumeData(prev => ({
            ...prev,
            personalInfo: { ...prev.personalInfo, summary: response.data.improved_text }
          }));
        } else if (fieldName === 'experience_description') {
          setExpInput(prev => ({ ...prev, description: response.data.improved_text }));
        } else if (fieldName === 'project_description') {
          setProjInput(prev => ({ ...prev, description: response.data.improved_text }));
        }
        setAiContext(''); // clear context
      }
    } catch (err) {
      console.error(err);
      showAlert(t('resumeBuilder.alerts.aiImprovementError', 'AI Improvement failed. Please try again.'), t('resumeBuilder.alerts.aiErrorTitle', 'AI Error'), "error");
    } finally {
      setImprovingField(null);
    }
  };

  // Job matching trigger
  const runJobMatch = async () => {
    if (!selectedJobId) {
      showAlert(t('resumeBuilder.alerts.jobMatchSelectionWarning', 'Please select a job description to match against.'), t('resumeBuilder.alerts.jobMatchSelectionTitle', 'Job Match Selection'), "warning");
      return;
    }

    setJobMatchLoading(true);
    try {
      // We check if the user has a saved resume, if not save it first
      let currentResumeId = resumeId;
      if (!currentResumeId) {
        await saveDraft();
      }

      // Re-fetch resumes to ensure we have the active ID updated in local variables
      const checkResumes = await api.get('resumes/');
      const latestResume = checkResumes.data.results.find(r => r.is_active) || checkResumes.data.results[0];
      if (latestResume) {
        currentResumeId = latestResume.id;
      }

      if (!currentResumeId) {
        throw new Error("Unable to save resume for matching.");
      }

      // Trigger the preview-match endpoint
      const response = await api.get(`public-jobs/${selectedJobId}/preview-match/?resume_id=${currentResumeId}`);
      if (response.data && response.data.matching_score !== undefined) {
        setJobMatchScore(Math.round(response.data.matching_score));
      } else {
        // Fallback random mock score if semantic match is not computed
        setJobMatchScore(78);
      }
    } catch (err) {
      console.error(err);
      // Fallback fallback score
      setJobMatchScore(78);
    } finally {
      setJobMatchLoading(false);
    }
  };

  // List management helpers
  const handleAddSkill = () => {
    if (skillInput.trim() && !resumeData.skills.includes(skillInput.trim())) {
      setResumeData(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill) => {
    setResumeData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const handleAddEdu = () => {
    if (!eduInput.school || !eduInput.degree) {
      showAlert(t('resumeBuilder.alerts.schoolDegreeRequired', 'School and Degree are required.'), t('resumeBuilder.alerts.validationErrorTitle', 'Validation Error'), "warning");
      return;
    }

    if (editingIndex.type === 'edu' && editingIndex.index !== null) {
      const updated = [...resumeData.education];
      updated[editingIndex.index] = eduInput;
      setResumeData(prev => ({ ...prev, education: updated }));
      setEditingIndex({ type: '', index: null });
    } else {
      setResumeData(prev => ({ ...prev, education: [...prev.education, eduInput] }));
    }

    setEduInput({ school: '', degree: '', fieldOfStudy: '', startYear: '', endYear: '', grade: '', location: '', description: '' });
  };

  const handleAddExp = () => {
    if (!expInput.jobTitle || !expInput.company) {
      showAlert(t('resumeBuilder.alerts.jobTitleCompanyRequired', 'Job Title and Company are required.'), t('resumeBuilder.alerts.validationErrorTitle', 'Validation Error'), "warning");
      return;
    }

    if (editingIndex.type === 'exp' && editingIndex.index !== null) {
      const updated = [...resumeData.experience];
      updated[editingIndex.index] = expInput;
      setResumeData(prev => ({ ...prev, experience: updated }));
      setEditingIndex({ type: '', index: null });
    } else {
      setResumeData(prev => ({ ...prev, experience: [...prev.experience, expInput] }));
    }

    setExpInput({ jobTitle: '', company: '', location: '', startDate: '', endDate: '', isCurrent: false, description: '' });
  };

  const handleAddProj = () => {
    if (!projInput.name) {
      showAlert(t('resumeBuilder.alerts.projectNameRequired', 'Project Name is required.'), t('resumeBuilder.alerts.validationErrorTitle', 'Validation Error'), "warning");
      return;
    }

    if (editingIndex.type === 'proj' && editingIndex.index !== null) {
      const updated = [...resumeData.projects];
      updated[editingIndex.index] = projInput;
      setResumeData(prev => ({ ...prev, projects: updated }));
      setEditingIndex({ type: '', index: null });
    } else {
      setResumeData(prev => ({ ...prev, projects: [...prev.projects, projInput] }));
    }

    setProjInput({ name: '', role: '', link: '', technologies: '', description: '' });
  };

  const handleAddCert = () => {
    if (!certInput.name) {
      showAlert(t('resumeBuilder.alerts.certNameRequired', 'Certification Name is required.'), t('resumeBuilder.alerts.validationErrorTitle', 'Validation Error'), "warning");
      return;
    }

    if (editingIndex.type === 'cert' && editingIndex.index !== null) {
      const updated = [...resumeData.certifications];
      updated[editingIndex.index] = certInput;
      setResumeData(prev => ({ ...prev, certifications: updated }));
      setEditingIndex({ type: '', index: null });
    } else {
      setResumeData(prev => ({ ...prev, certifications: [...prev.certifications, certInput] }));
    }

    setCertInput({ name: '', organization: '', date: '', credentialId: '', credentialUrl: '' });
  };

  const handleAddAward = () => {
    if (!awardInput.title) {
      showAlert(t('resumeBuilder.alerts.awardTitleRequired', 'Achievement Title is required.'), t('resumeBuilder.alerts.validationErrorTitle', 'Validation Error'), "warning");
      return;
    }

    if (editingIndex.type === 'award' && editingIndex.index !== null) {
      const updated = [...resumeData.achievements];
      updated[editingIndex.index] = awardInput;
      setResumeData(prev => ({ ...prev, achievements: updated }));
      setEditingIndex({ type: '', index: null });
    } else {
      setResumeData(prev => ({ ...prev, achievements: [...prev.achievements, awardInput] }));
    }

    setAwardInput({ title: '', date: '', description: '' });
  };

  // Editing trigger helpers
  const startEdit = (type, index, item) => {
    setEditingIndex({ type, index });
    if (type === 'edu') setEduInput(item);
    if (type === 'exp') setExpInput(item);
    if (type === 'proj') setProjInput(item);
    if (type === 'cert') setCertInput(item);
    if (type === 'award') setAwardInput(item);
  };

  const removeItem = (type, index) => {
    setResumeData(prev => {
      const list = [...prev[type]];
      list.splice(index, 1);
      return { ...prev, [type]: list };
    });
  };

  // Theme palettes helper
  const themeColors = [
    { name: 'Indigo', hex: '#4f46e5' },
    { name: 'Emerald', hex: '#10b981' },
    { name: 'Slate Blue', hex: '#0284c7' },
    { name: 'Teal', hex: '#0f766e' },
    { name: 'Rose', hex: '#e11d48' }
  ];

  const templates = [
    { 
      id: 'modern_professional', 
      name: 'Modern Professional', 
      desc: 'Sleek two-column layout, dark sidebar',
      badge: 'RECOMMENDED',
      category: 'Professional',
      atsScore: 92,
      mini: (
        <div className="w-full h-full bg-slate-50 flex gap-0.5 p-0.5">
          <div className="w-[30%] h-full rounded" style={{ backgroundColor: '#0f172a' }}></div>
          <div className="w-[70%] h-full flex flex-col gap-1 p-0.5">
            <div className="h-1.5 bg-slate-350 w-3/4 rounded-xs"></div>
            <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
            <div className="h-0.5 bg-slate-200 w-5/6 rounded-xs"></div>
            <div className="h-0.5 bg-slate-200 w-2/3 rounded-xs"></div>
          </div>
        </div>
      )
    },
    { 
      id: 'minimalist_clean', 
      name: 'Minimalist Clean', 
      desc: 'Elegant one-column clean design',
      badge: 'RECOMMENDED',
      category: 'Professional',
      atsScore: 98,
      mini: (
        <div className="w-full h-full bg-white flex flex-col gap-1 p-1 items-center">
          <div className="h-1.5 bg-slate-350 w-1/2 rounded-xs mb-0.5"></div>
          <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
          <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
          <div className="h-0.5 bg-slate-200 w-5/6 rounded-xs"></div>
        </div>
      )
    },
    { 
      id: 'creative_tech', 
      name: 'Creative Tech', 
      desc: 'Accented top banner tech layout',
      badge: 'MODERN',
      category: 'Creative',
      atsScore: 85,
      mini: (
        <div className="w-full h-full bg-white flex flex-col gap-0.5">
          <div className="h-2.5 w-full" style={{ backgroundColor: resumeData.themeColor }}></div>
          <div className="flex-grow flex gap-0.5 p-0.5">
            <div className="w-full flex flex-col gap-1">
              <div className="h-1 bg-slate-350 w-1/3 rounded-xs"></div>
              <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
              <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
            </div>
          </div>
        </div>
      )
    },
    { 
      id: 'executive_royal', 
      name: 'Executive Royal', 
      desc: 'Deep navy and gold accent divider lines',
      badge: 'POPULAR',
      category: 'Professional',
      atsScore: 95,
      mini: (
        <div className="w-full h-full bg-white flex flex-col gap-1 p-1">
          <div className="h-2 w-full border-b-2 flex flex-col gap-0.5 pb-0.5" style={{ borderColor: resumeData.themeColor }}>
            <div className="h-1 bg-slate-800 w-1/2 rounded-xs"></div>
          </div>
          <div className="flex-grow flex flex-col gap-1">
            <div className="h-0.5 bg-slate-300 w-full rounded-xs"></div>
            <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
            <div className="h-0.5 bg-slate-200 w-3/4 rounded-xs"></div>
          </div>
        </div>
      )
    },
    { 
      id: 'academic_cv', 
      name: 'Academic CV', 
      desc: 'Curriculum Vitae classic serif layout',
      badge: 'CLASSIC',
      category: 'Academic',
      atsScore: 99,
      mini: (
        <div className="w-full h-full bg-white flex flex-col gap-1 p-1 items-center" style={{ fontFamily: 'Georgia, serif' }}>
          <div className="h-1 bg-slate-900 w-3/4 rounded-xs text-[4px] font-bold text-center leading-[3px] mb-0.5">CV</div>
          <div className="w-full flex gap-1 items-start">
            <div className="w-1/3 h-0.5 bg-slate-300 rounded-xs"></div>
            <div className="w-2/3 flex flex-col gap-0.5">
              <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
              <div className="h-0.5 bg-slate-200 w-5/6 rounded-xs"></div>
            </div>
          </div>
        </div>
      )
    },
    { 
      id: 'elegant_sidebar', 
      name: 'Elegant Sidebar', 
      desc: 'Left column sidebar for profile & info',
      badge: 'CREATIVE',
      category: 'Creative',
      atsScore: 88,
      mini: (
        <div className="w-full h-full bg-white flex gap-0.5 p-0.5">
          <div className="w-[30%] bg-slate-100 h-full rounded flex flex-col items-center p-0.5 gap-1">
            <div className="h-3 w-3 rounded-full" style={{ backgroundColor: resumeData.themeColor }}></div>
            <div className="h-0.5 bg-slate-300 w-3/4 rounded-xs"></div>
          </div>
          <div className="w-[70%] h-full flex flex-col gap-1 p-0.5">
            <div className="h-1 bg-slate-350 w-3/4 rounded-xs"></div>
            <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
            <div className="h-0.5 bg-slate-200 w-5/6 rounded-xs"></div>
          </div>
        </div>
      )
    },
    { 
      id: 'bold_minimal', 
      name: 'Bold Minimal', 
      desc: 'Clean layout with solid banner top bar',
      badge: 'BOLD',
      category: 'Professional',
      atsScore: 94,
      mini: (
        <div className="w-full h-full bg-white flex flex-col gap-0.5">
          <div className="h-4 w-full flex items-center justify-center p-0.5" style={{ backgroundColor: resumeData.themeColor }}>
            <div className="h-1 bg-white w-2/3 rounded-xs"></div>
          </div>
          <div className="bg-slate-900 h-1.5 w-full"></div>
          <div className="flex-grow flex flex-col gap-1 p-1">
            <div className="h-0.5 bg-slate-300 w-1/2 rounded-xs"></div>
            <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
          </div>
        </div>
      )
    },
    { 
      id: 'hybrid_split', 
      name: 'Hybrid Split', 
      desc: 'Balanced equal split columns and header',
      badge: 'BALANCED',
      category: 'Professional',
      atsScore: 90,
      mini: (
        <div className="w-full h-full bg-white flex flex-col gap-0.5 p-1">
          <div className="h-1 bg-slate-350 w-1/2 rounded-xs self-center mb-0.5"></div>
          <div className="flex gap-1 flex-grow">
            <div className="w-1/2 flex flex-col gap-0.5">
              <div className="h-0.5 bg-slate-300 w-full rounded-xs"></div>
              <div className="h-0.5 bg-slate-200 w-5/6 rounded-xs"></div>
            </div>
            <div className="w-[1px] bg-slate-200 self-stretch"></div>
            <div className="w-1/2 flex flex-col gap-0.5">
              <div className="h-0.5 bg-slate-300 w-3/4 rounded-xs"></div>
              <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
            </div>
          </div>
        </div>
      )
    },
    { 
      id: 'creative_marketing', 
      name: 'Creative Marketing', 
      desc: 'Friendly bubble skill tags & highlights',
      badge: 'STYLISH',
      category: 'Creative',
      atsScore: 80,
      mini: (
        <div className="w-full h-full bg-white flex flex-col gap-1 p-1">
          <div className="w-full flex justify-between items-center mb-0.5">
            <div className="h-1 w-1/2 bg-slate-800 rounded-xs"></div>
            <div className="h-1 w-1/4 bg-slate-300 rounded-xs"></div>
          </div>
          <div className="h-1.5 w-full rounded-xs" style={{ backgroundColor: `${resumeData.themeColor}1a` }}></div>
          <div className="flex flex-col gap-0.5 mt-0.5">
            <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
            <div className="flex gap-0.5">
              <div className="h-1 w-3 rounded-full" style={{ backgroundColor: `${resumeData.themeColor}33` }}></div>
              <div className="h-1 w-3 rounded-full" style={{ backgroundColor: `${resumeData.themeColor}33` }}></div>
            </div>
          </div>
        </div>
      )
    },
    { 
      id: 'corporate_elite', 
      name: 'Corporate Elite', 
      desc: 'Elegant corporate layout with a prominent color accent bar',
      badge: 'PREMIUM',
      category: 'Professional',
      atsScore: 96,
      mini: (
        <div className="w-full h-full bg-white flex flex-col gap-1 p-1">
          <div className="h-1.5 w-full rounded-xs mb-0.5" style={{ backgroundColor: resumeData.themeColor }}></div>
          <div className="w-full flex flex-col items-center gap-0.5 mb-1">
            <div className="h-1.5 bg-slate-800 w-2/3 rounded-xs"></div>
            <div className="h-0.5 bg-slate-350 w-1/3 rounded-xs"></div>
          </div>
          <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
          <div className="h-0.5 bg-slate-200 w-full rounded-xs"></div>
          <div className="h-0.5 bg-slate-200 w-5/6 rounded-xs"></div>
        </div>
      )
    }
  ];

  return (
    <PageShell fill={false} className="bg-[#fafafc] pb-12" style={{ fontFamily: "'Inter', 'Poppins', sans-serif" }}>
      <PageCard overflow={false}>
          <PageHeader
            title={
              <>
                <DescriptionIcon className="text-white" sx={{ fontSize: 20 }} />
                {t('resumeBuilder.title', 'Resume Builder')}
              </>
            }
            subtitle={t('resumeBuilder.subtitle', 'Create ATS-friendly resumes in minutes with AI assistance.')}
          >
            <div className="flex items-center rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-bold text-white">
              <span className={`mr-2 h-2 w-2 rounded-full ${saveStatus === 'All changes saved' ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'}`}></span>
              <span>{getSaveStatusLabel(saveStatus)}</span>
            </div>
            <PageHeaderButton
              onClick={saveDraft}
              disabled={saving}
              icon={<SaveIcon sx={{ fontSize: 16 }} />}
            >
              {saving ? t('resumeBuilder.buttons.saving', 'Saving...') : t('resumeBuilder.buttons.saveDraft', 'Save Draft')}
            </PageHeaderButton>
            <PageHeaderButton
              onClick={downloadPDF}
              disabled={downloading}
              color="indigo"
              icon={<DownloadIcon sx={{ fontSize: 16 }} />}
            >
              {downloading ? t('resumeBuilder.buttons.downloading', 'Downloading...') : t('resumeBuilder.buttons.downloadPdf', 'Download PDF')}
            </PageHeaderButton>
          </PageHeader>
        <div className="w-full p-3 sm:p-4">
          <div className="flex flex-col lg:flex-row gap-3.5 items-stretch">
            {/* Column 1: Left Navigation Stepper (width lg:w-[21%]) */}
            <div className="w-full lg:w-[21%] flex flex-col gap-6 flex-shrink-0">
              <div className="bg-white p-5 rounded-xl border border-slate-200/80 shadow-sm flex flex-col gap-5">
                <div>
                  <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">{t('resumeBuilder.sidebar.title', 'BUILD YOUR RESUME')}</div>
                  <div className="space-y-1">
                    {[
                      { num: 1, name: 'Personal Information' },
                      { num: 2, name: 'Education' },
                      { num: 3, name: 'Experience' },
                      { num: 4, name: 'Projects' },
                      { num: 5, name: 'Skills' },
                      { num: 6, name: 'Certifications' },
                      { num: 7, name: 'Achievements' },
                      { num: 8, name: 'AI Resume Review' }
                    ].map(step => {
                      const completed = isStepCompleted(step.num);
                      const active = activeStep === step.num;
                      return (
                        <button
                          key={step.num}
                          onClick={() => setActiveStep(step.num)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-semibold text-left transition-all ${
                            active
                              ? 'bg-indigo-50/70 text-indigo-700 font-bold'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {completed ? (
                            <div className="h-5 w-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0 text-emerald-600">
                              <CheckCircleIcon sx={{ fontSize: 14 }} />
                            </div>
                          ) : (
                            <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[11px] font-bold border flex-shrink-0 ${
                              active
                                ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm'
                                : 'border-slate-300 bg-white text-slate-400'
                            }`}>
                              {step.num}
                            </div>
                          )}
                          <span className="truncate">{t(getStepTranslationKey(step.num), step.name)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button
                  onClick={() => setIsFullScreenOpen(true)}
                  className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-indigo-600 py-2.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50/40 transition-all active:scale-95 shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4" />
                  </svg>
                  {t('resumeBuilder.buttons.previewFullScreen', 'Preview Full Screen')}
                </button>

                <div className="bg-amber-50/40 border border-amber-100 rounded-xl p-4 flex flex-col gap-2">
                  <div className="flex items-center gap-1.5 text-amber-600 font-bold text-xs">
                    <span>💡</span> {t('resumeBuilder.sidebar.tipTitle', 'Tip')}
                  </div>
                  <p className="text-[11px] text-slate-500 leading-relaxed font-medium">
                    {t('resumeBuilder.sidebar.tipContent', 'Complete all sections to improve your ATS score and job match.')}
                  </p>
                  <button
                    onClick={() => showAlert(t('resumeBuilder.alerts.profileCompletenessTips', "Fill in all the sections, add keywords matching target job descriptions, and ensure skills have CGPAs/details to reach 100% completion."), t('resumeBuilder.alerts.profileCompletenessTipsTitle', "Profile Completeness Tips"), "info")}
                    className="text-[11px] font-bold text-indigo-600 hover:underline text-left mt-1"
                  >
                    {t('resumeBuilder.buttons.learnMore', 'Learn more ➔')}
                  </button>
                </div>
              </div>
            </div>

            {/* Column 2: Form Workspace (width lg:w-[34%]) */}
            <div className="w-full lg:w-[34%] flex-shrink-0">
              <div className="bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm">
                <div>
                  {/* Personal Information Form */}
                  {activeStep === 1 && (
                    <div>
                      <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                        <h2 className="text-sm font-bold text-slate-800">{t('resumeBuilder.personalInfo.title', 'Personal Information')}</h2>
                        <button
                          onClick={() => handleAiImprove('summary', resumeData.personalInfo.summary || 'Write a developer professional summary.')}
                          disabled={improvingField !== null}
                          className="flex items-center gap-1 bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-1 rounded-md text-[11px] font-semibold hover:bg-indigo-100 transition-all"
                        >
                          <SparklesIcon sx={{ fontSize: 12 }} />
                          {t('resumeBuilder.buttons.generateSummaryWithAi', 'Generate Summary with AI')}
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('resumeBuilder.personalInfo.fullName', 'Full Name')} <span className="text-rose-500">*</span></label>
                          <input
                            type="text"
                            value={resumeData.personalInfo.fullName}
                            onChange={e => setResumeData(prev => ({
                              ...prev,
                              personalInfo: { ...prev.personalInfo, fullName: e.target.value }
                            }))}
                            className="w-full text-sm font-medium px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white shadow-sm"
                            placeholder={t('resumeBuilder.personalInfo.fullNamePlaceholder', 'e.g. Samiksha Jain')}
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('resumeBuilder.personalInfo.professionalTitle', 'Professional Title')} <span className="text-rose-500">*</span></label>
                          <input
                            type="text"
                            value={resumeData.personalInfo.title}
                            onChange={e => setResumeData(prev => ({
                              ...prev,
                              personalInfo: { ...prev.personalInfo, title: e.target.value }
                            }))}
                            className="w-full text-sm font-medium px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white shadow-sm"
                            placeholder={t('resumeBuilder.personalInfo.professionalTitlePlaceholder', 'e.g. Java Developer')}
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('resumeBuilder.personalInfo.email', 'Email')} <span className="text-rose-500">*</span></label>
                          <input
                            type="email"
                            value={resumeData.personalInfo.email}
                            onChange={e => setResumeData(prev => ({
                              ...prev,
                              personalInfo: { ...prev.personalInfo, email: e.target.value }
                            }))}
                            className="w-full text-sm font-medium px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white shadow-sm"
                            placeholder={t('resumeBuilder.personalInfo.emailPlaceholder', 'e.g. samiksha2001jain@gmail.com')}
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('resumeBuilder.personalInfo.phone', 'Phone')}</label>
                          <input
                            type="text"
                            value={resumeData.personalInfo.phone}
                            onChange={e => setResumeData(prev => ({
                              ...prev,
                              personalInfo: { ...prev.personalInfo, phone: e.target.value }
                            }))}
                            className="w-full text-sm font-medium px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white shadow-sm"
                            placeholder={t('resumeBuilder.personalInfo.phonePlaceholder', 'e.g. +91 99987 65432')}
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('resumeBuilder.personalInfo.location', 'Location')} <span className="text-rose-500">*</span></label>
                          <input
                            type="text"
                            value={resumeData.personalInfo.location}
                            onChange={e => setResumeData(prev => ({
                              ...prev,
                              personalInfo: { ...prev.personalInfo, location: e.target.value }
                            }))}
                            className="w-full text-sm font-medium px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white shadow-sm"
                            placeholder={t('resumeBuilder.personalInfo.locationPlaceholder', 'e.g. Indore, Madhya Pradesh, India')}
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('resumeBuilder.personalInfo.linkedin', 'LinkedIn')}</label>
                          <input
                            type="text"
                            value={resumeData.personalInfo.linkedin}
                            onChange={e => setResumeData(prev => ({
                              ...prev,
                              personalInfo: { ...prev.personalInfo, linkedin: e.target.value }
                            }))}
                            className="w-full text-sm font-medium px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white shadow-sm"
                            placeholder={t('resumeBuilder.personalInfo.linkedinPlaceholder', 'e.g. linkedin.com/in/samiksha-jain')}
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('resumeBuilder.personalInfo.github', 'GitHub')}</label>
                          <input
                            type="text"
                            value={resumeData.personalInfo.github}
                            onChange={e => setResumeData(prev => ({
                              ...prev,
                              personalInfo: { ...prev.personalInfo, github: e.target.value }
                            }))}
                            className="w-full text-sm font-medium px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white shadow-sm"
                            placeholder={t('resumeBuilder.personalInfo.githubPlaceholder', 'e.g. github.com/samikshajain')}
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('resumeBuilder.personalInfo.website', 'Portfolio Website')}</label>
                          <input
                            type="text"
                            value={resumeData.personalInfo.website}
                            onChange={e => setResumeData(prev => ({
                              ...prev,
                              personalInfo: { ...prev.personalInfo, website: e.target.value }
                            }))}
                            className="w-full text-sm font-medium px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white shadow-sm"
                            placeholder={t('resumeBuilder.personalInfo.websitePlaceholder', 'e.g. https://samikshajain.dev')}
                          />
                        </div>

                        <div className="md:col-span-2">
                          <div className="flex justify-between items-center mb-1">
                            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.personalInfo.summary', 'Professional Summary')} <span className="text-rose-500">*</span></label>
                            <span className="text-[11px] font-semibold text-slate-400">{(resumeData.personalInfo.summary || '').length}/500</span>
                          </div>
                          <textarea
                            rows="4"
                            value={resumeData.personalInfo.summary}
                            onChange={e => setResumeData(prev => ({
                              ...prev,
                              personalInfo: { ...prev.personalInfo, summary: e.target.value.slice(0, 500) }
                            }))}
                            className="w-full text-sm font-medium p-3 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 resize-none leading-relaxed bg-white shadow-sm"
                            placeholder={t('resumeBuilder.personalInfo.summaryPlaceholder', 'e.g. Motivated Java Developer with strong knowledge of Java, Spring Boot, REST APIs, and SQL...')}
                          />

                          <div className="flex items-center gap-3 mt-2">
                            <input
                              type="text"
                              value={aiContext}
                              onChange={e => setAiContext(e.target.value)}
                              placeholder={t('resumeBuilder.personalInfo.aiContextPlaceholder', 'Context (e.g. Make it more professional/senior...)')}
                              className="flex-grow text-[11px] font-medium px-3 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:border-indigo-500 bg-white"
                            />
                            <button
                              onClick={() => handleAiImprove('summary', resumeData.personalInfo.summary)}
                              disabled={improvingField !== null}
                              className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1.5 rounded-md text-[11px] font-bold hover:bg-indigo-100 transition-all disabled:opacity-50"
                            >
                              <SparklesIcon sx={{ fontSize: 12 }} />
                              {t('resumeBuilder.buttons.improveWithAi', 'Improve with AI')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Education Step */}
                  {activeStep === 2 && (
                    <div>
                      <div className="border-b border-slate-100 pb-3 mb-4">
                        <h2 className="text-sm font-bold text-slate-800">{t('resumeBuilder.education.title', 'Education')}</h2>
                      </div>

                      {/* Added Items List */}
                      {resumeData.education.length > 0 && (
                        <div className="mb-6 space-y-2">
                          {resumeData.education.map((edu, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100">
                              <div>
                                <div className="text-xs font-bold text-slate-800">{edu.degree} - {edu.school}</div>
                                <div className="text-[11px] text-slate-500">{edu.startYear} - {edu.endYear} {edu.grade ? `| CGPA: ${edu.grade}` : ''}</div>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => startEdit('edu', idx, edu)} className="text-indigo-600 hover:text-indigo-800"><EditIcon sx={{ fontSize: 16 }} /></button>
                                <button onClick={() => removeItem('education', idx)} className="text-rose-600 hover:text-rose-800"><DeleteIcon sx={{ fontSize: 16 }} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Input Form */}
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                        <h3 className="text-xs font-bold text-slate-700">{editingIndex.type === 'edu' ? t('resumeBuilder.education.editTitle', 'Edit Education') : t('resumeBuilder.education.addTitle', 'Add Education')}</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.education.school', 'School/University')}</label>
                            <input
                              type="text"
                              value={eduInput.school}
                              onChange={e => setEduInput({ ...eduInput, school: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.education.degree', 'Degree')}</label>
                            <input
                              type="text"
                              value={eduInput.degree}
                              onChange={e => setEduInput({ ...eduInput, degree: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                              placeholder={t('resumeBuilder.education.degreePlaceholder', 'e.g. Master of Computer Applications')}
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.education.fieldOfStudy', 'Field of Study')}</label>
                            <input
                              type="text"
                              value={eduInput.fieldOfStudy}
                              onChange={e => setEduInput({ ...eduInput, fieldOfStudy: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                              placeholder={t('resumeBuilder.education.fieldOfStudyPlaceholder', 'e.g. Computer Science')}
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.education.startYear', 'Start Year')}</label>
                            <input
                              type="text"
                              value={eduInput.startYear}
                              onChange={e => setEduInput({ ...eduInput, startYear: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.education.endYear', 'End Year')}</label>
                            <input
                              type="text"
                              value={eduInput.endYear}
                              onChange={e => setEduInput({ ...eduInput, endYear: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.education.grade', 'Grade / CGPA')}</label>
                            <input
                              type="text"
                              value={eduInput.grade}
                              onChange={e => setEduInput({ ...eduInput, grade: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.education.location', 'Location')}</label>
                            <input
                              type="text"
                              value={eduInput.location}
                              onChange={e => setEduInput({ ...eduInput, location: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                        </div>
                        <button
                          onClick={handleAddEdu}
                          className="w-full bg-indigo-600 text-white rounded-md py-1.5 text-xs font-bold hover:bg-indigo-700 transition-all flex items-center justify-center gap-1.5"
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                          {editingIndex.type === 'edu' ? t('resumeBuilder.buttons.saveChanges', 'Save Changes') : t('resumeBuilder.buttons.addToResume', 'Add to Resume')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Experience Step */}
                  {activeStep === 3 && (
                    <div>
                      <div className="border-b border-slate-100 pb-3 mb-4">
                        <h2 className="text-sm font-bold text-slate-800">{t('resumeBuilder.experience.title', 'Experience')}</h2>
                      </div>

                      {/* Added Items List */}
                      {resumeData.experience.length > 0 && (
                        <div className="mb-6 space-y-2">
                          {resumeData.experience.map((exp, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100">
                              <div>
                                <div className="text-xs font-bold text-slate-800">{exp.jobTitle} - {exp.company}</div>
                                <div className="text-[11px] text-slate-500">{exp.startDate} - {exp.isCurrent ? t('resumeBuilder.experience.present', 'Present') : exp.endDate}</div>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => startEdit('exp', idx, exp)} className="text-indigo-600 hover:text-indigo-800"><EditIcon sx={{ fontSize: 16 }} /></button>
                                <button onClick={() => removeItem('experience', idx)} className="text-rose-600 hover:text-rose-800"><DeleteIcon sx={{ fontSize: 16 }} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Input Form */}
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                        <h3 className="text-xs font-bold text-slate-700">{editingIndex.type === 'exp' ? t('resumeBuilder.experience.editTitle', 'Edit Experience') : t('resumeBuilder.experience.addTitle', 'Add Experience')}</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.experience.jobTitle', 'Job Title')}</label>
                            <input
                              type="text"
                              value={expInput.jobTitle}
                              onChange={e => setExpInput({ ...expInput, jobTitle: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.experience.company', 'Company')}</label>
                            <input
                              type="text"
                              value={expInput.company}
                              onChange={e => setExpInput({ ...expInput, company: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.experience.startDate', 'Start Date')}</label>
                            <input
                              type="text"
                              value={expInput.startDate}
                              onChange={e => setExpInput({ ...expInput, startDate: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                              placeholder={t('resumeBuilder.experience.startDatePlaceholder', 'e.g. Jan 2024')}
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.experience.endDate', 'End Date')}</label>
                            <input
                              type="text"
                              value={expInput.endDate}
                              onChange={e => setExpInput({ ...expInput, endDate: e.target.value })}
                              disabled={expInput.isCurrent}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500 disabled:bg-slate-100"
                              placeholder={t('resumeBuilder.experience.endDatePlaceholder', 'e.g. Present')}
                            />
                          </div>
                          <div className="col-span-2 flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="isCurrentExp"
                              checked={expInput.isCurrent}
                              onChange={e => setExpInput({ ...expInput, isCurrent: e.target.checked, endDate: e.target.checked ? 'Present' : '' })}
                              className="rounded text-indigo-600 focus:ring-indigo-500"
                            />
                            <label htmlFor="isCurrentExp" className="text-sm font-medium text-slate-600">{t('resumeBuilder.experience.isCurrent', 'I currently work here')}</label>
                          </div>
                          <div className="col-span-2">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.experience.description', 'Description (one bullet point per line)')}</label>
                            <textarea
                              rows="3"
                              value={expInput.description}
                              onChange={e => setExpInput({ ...expInput, description: e.target.value })}
                              className="w-full text-sm font-medium p-2 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500 resize-none"
                              placeholder={t('resumeBuilder.experience.descriptionPlaceholder', 'e.g. - Developed RESTful APIs using Spring Boot.')}
                            />
                            <button
                              onClick={() => handleAiImprove('experience_description', expInput.description)}
                              disabled={improvingField !== null}
                              className="flex items-center gap-1 mt-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 transition-all disabled:opacity-50"
                            >
                              <SparklesIcon sx={{ fontSize: 12 }} />
                              {t('resumeBuilder.buttons.improveDescriptionWithAi', 'Improve Description with AI')}
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={handleAddExp}
                          className="w-full bg-indigo-600 text-white rounded-md py-1.5 text-xs font-bold hover:bg-indigo-700 transition-all flex items-center justify-center gap-1.5"
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                          {editingIndex.type === 'exp' ? t('resumeBuilder.buttons.saveChanges', 'Save Changes') : t('resumeBuilder.buttons.addToResume', 'Add to Resume')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Projects Step */}
                  {activeStep === 4 && (
                    <div>
                      <div className="border-b border-slate-100 pb-3 mb-4">
                        <h2 className="text-sm font-bold text-slate-800">{t('resumeBuilder.projects.title', 'Projects')}</h2>
                      </div>

                      {/* Added Items List */}
                      {resumeData.projects.length > 0 && (
                        <div className="mb-6 space-y-2">
                          {resumeData.projects.map((p, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100">
                              <div>
                                <div className="text-xs font-bold text-slate-800">{p.name}</div>
                                <div className="text-[11px] text-slate-500">{p.role || 'Contributor'}</div>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => startEdit('proj', idx, p)} className="text-indigo-600 hover:text-indigo-800"><EditIcon sx={{ fontSize: 16 }} /></button>
                                <button onClick={() => removeItem('projects', idx)} className="text-rose-600 hover:text-rose-800"><DeleteIcon sx={{ fontSize: 16 }} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Input Form */}
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                        <h3 className="text-xs font-bold text-slate-700">{editingIndex.type === 'proj' ? t('resumeBuilder.projects.editTitle', 'Edit Project') : t('resumeBuilder.projects.addTitle', 'Add Project')}</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.projects.name', 'Project Name')}</label>
                            <input
                              type="text"
                              value={projInput.name}
                              onChange={e => setProjInput({ ...projInput, name: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.projects.role', 'Role')}</label>
                            <input
                              type="text"
                              value={projInput.role}
                              onChange={e => setProjInput({ ...projInput, role: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.projects.link', 'Project Link')}</label>
                            <input
                              type="text"
                              value={projInput.link}
                              onChange={e => setProjInput({ ...projInput, link: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.projects.technologies', 'Technologies Used (e.g. React, Spring Boot, MySQL)')}</label>
                            <input
                              type="text"
                              value={projInput.technologies}
                              onChange={e => setProjInput({ ...projInput, technologies: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.projects.description', 'Description (one bullet point per line)')}</label>
                            <textarea
                              rows="3"
                              value={projInput.description}
                              onChange={e => setProjInput({ ...projInput, description: e.target.value })}
                              className="w-full text-sm font-medium p-2 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500 resize-none"
                            />
                            <button
                              onClick={() => handleAiImprove('project_description', projInput.description)}
                              disabled={improvingField !== null}
                              className="flex items-center gap-1 mt-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 transition-all disabled:opacity-50"
                            >
                              <SparklesIcon sx={{ fontSize: 12 }} />
                              {t('resumeBuilder.buttons.improveDescriptionWithAi', 'Improve Description with AI')}
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={handleAddProj}
                          className="w-full bg-indigo-600 text-white rounded-md py-1.5 text-xs font-bold hover:bg-indigo-700 transition-all flex items-center justify-center gap-1.5"
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                          {editingIndex.type === 'proj' ? t('resumeBuilder.buttons.saveChanges', 'Save Changes') : t('resumeBuilder.buttons.addToResume', 'Add to Resume')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Skills Step */}
                  {activeStep === 5 && (
                    <div>
                      <div className="border-b border-slate-100 pb-3 mb-4">
                        <h2 className="text-sm font-bold text-slate-800">{t('resumeBuilder.skills.title', 'Skills')}</h2>
                      </div>

                      <div className="flex gap-2 mb-6">
                        <input
                          type="text"
                          value={skillInput}
                          onChange={e => setSkillInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddSkill();
                            }
                          }}
                          placeholder={t('resumeBuilder.skills.placeholder', 'Add a skill (e.g. Spring Boot) and press Enter')}
                          className="flex-grow text-sm font-medium px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500"
                        />
                        <button
                          onClick={handleAddSkill}
                          className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-indigo-700 transition-all active:scale-95"
                        >
                          {t('resumeBuilder.buttons.addSkill', 'Add')}
                        </button>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {resumeData.skills.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">{t('resumeBuilder.skills.noSkills', 'No skills added yet.')}</p>
                        ) : (
                          resumeData.skills.map((skill, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 text-xs font-bold text-slate-700 shadow-sm"
                            >
                              <span>{skill}</span>
                              <button
                                onClick={() => handleRemoveSkill(skill)}
                                className="text-slate-400 hover:text-slate-600"
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Certifications Step */}
                  {activeStep === 6 && (
                    <div>
                      <div className="border-b border-slate-100 pb-3 mb-4">
                        <h2 className="text-sm font-bold text-slate-800">{t('resumeBuilder.certifications.title', 'Certifications')}</h2>
                      </div>

                      {/* Added Items List */}
                      {resumeData.certifications.length > 0 && (
                        <div className="mb-6 space-y-2">
                          {resumeData.certifications.map((c, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100">
                              <div>
                                <div className="text-xs font-bold text-slate-800">{c.name}</div>
                                <div className="text-[11px] text-slate-500">{c.organization} | {c.date}</div>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => startEdit('cert', idx, c)} className="text-indigo-600 hover:text-indigo-800"><EditIcon sx={{ fontSize: 16 }} /></button>
                                <button onClick={() => removeItem('certifications', idx)} className="text-rose-600 hover:text-rose-800"><DeleteIcon sx={{ fontSize: 16 }} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Input Form */}
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                        <h3 className="text-xs font-bold text-slate-700">{editingIndex.type === 'cert' ? t('resumeBuilder.certifications.editTitle', 'Edit Certification') : t('resumeBuilder.certifications.addTitle', 'Add Certification')}</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.certifications.name', 'Certification Name')}</label>
                            <input
                              type="text"
                              value={certInput.name}
                              onChange={e => setCertInput({ ...certInput, name: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.certifications.organization', 'Issuing Organization')}</label>
                            <input
                              type="text"
                              value={certInput.organization}
                              onChange={e => setCertInput({ ...certInput, organization: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.certifications.date', 'Issue Date')}</label>
                            <input
                              type="text"
                              value={certInput.date}
                              onChange={e => setCertInput({ ...certInput, date: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                              placeholder={t('resumeBuilder.certifications.datePlaceholder', 'e.g. Nov 2024')}
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.certifications.credentialId', 'Credential ID')}</label>
                            <input
                              type="text"
                              value={certInput.credentialId}
                              onChange={e => setCertInput({ ...certInput, credentialId: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.certifications.credentialUrl', 'Credential URL')}</label>
                            <input
                              type="text"
                              value={certInput.credentialUrl}
                              onChange={e => setCertInput({ ...certInput, credentialUrl: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                        </div>
                        <button
                          onClick={handleAddCert}
                          className="w-full bg-indigo-600 text-white rounded-md py-1.5 text-xs font-bold hover:bg-indigo-700 transition-all flex items-center justify-center gap-1.5"
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                          {editingIndex.type === 'cert' ? t('resumeBuilder.buttons.saveChanges', 'Save Changes') : t('resumeBuilder.buttons.addToResume', 'Add to Resume')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Achievements Step */}
                  {activeStep === 7 && (
                    <div>
                      <div className="border-b border-slate-100 pb-3 mb-4">
                        <h2 className="text-sm font-bold text-slate-800">{t('resumeBuilder.achievements.title', 'Achievements')}</h2>
                      </div>

                      {/* Added Items List */}
                      {resumeData.achievements.length > 0 && (
                        <div className="mb-6 space-y-2">
                          {resumeData.achievements.map((a, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100">
                              <div>
                                <div className="text-xs font-bold text-slate-800">{a.title}</div>
                                <div className="text-[11px] text-slate-500">{a.date}</div>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => startEdit('award', idx, a)} className="text-indigo-600 hover:text-indigo-800"><EditIcon sx={{ fontSize: 16 }} /></button>
                                <button onClick={() => removeItem('achievements', idx)} className="text-rose-600 hover:text-rose-800"><DeleteIcon sx={{ fontSize: 16 }} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Input Form */}
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                        <h3 className="text-xs font-bold text-slate-700">{editingIndex.type === 'award' ? t('resumeBuilder.achievements.editTitle', 'Edit Achievement') : t('resumeBuilder.achievements.addTitle', 'Add Achievement')}</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.achievements.name', 'Achievement Title')}</label>
                            <input
                              type="text"
                              value={awardInput.title}
                              onChange={e => setAwardInput({ ...awardInput, title: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.achievements.date', 'Date')}</label>
                            <input
                              type="text"
                              value={awardInput.date}
                              onChange={e => setAwardInput({ ...awardInput, date: e.target.value })}
                              className="w-full text-sm font-medium px-2 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500"
                              placeholder={t('resumeBuilder.achievements.datePlaceholder', 'e.g. Dec 2024')}
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.achievements.description', 'Description')}</label>
                            <textarea
                              rows="2"
                              value={awardInput.description}
                              onChange={e => setAwardInput({ ...awardInput, description: e.target.value })}
                              className="w-full text-sm font-medium p-2 border border-slate-200 rounded-md bg-white focus:outline-none focus:border-indigo-500 resize-none"
                            />
                          </div>
                        </div>
                        <button
                          onClick={handleAddAward}
                          className="w-full bg-indigo-600 text-white rounded-md py-1.5 text-xs font-bold hover:bg-indigo-700 transition-all flex items-center justify-center gap-1.5"
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                          {editingIndex.type === 'award' ? t('resumeBuilder.buttons.saveChanges', 'Save Changes') : t('resumeBuilder.buttons.addToResume', 'Add to Resume')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* AI Resume Review Step */}
                  {activeStep === 8 && (
                    <div className="space-y-5">
                      <div className="border-b border-slate-100 pb-3 mb-4">
                        <h2 className="text-sm font-bold text-slate-800">{t('resumeBuilder.aiReview.title', 'AI Resume Review')}</h2>
                      </div>

                      {/* Resume Score Section */}
                      <div className="space-y-2.5">
                        <span className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.aiReview.dashboardTitle', 'Resume Score Dashboard')}</span>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {/* Resume Health */}
                          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/60 shadow-2xs">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-[10px] font-bold text-slate-550 uppercase tracking-wider">{t('resumeBuilder.aiReview.resumeHealth', 'Resume Health')}</span>
                              <span className="text-xs font-black text-indigo-650">{resumeHealth}%</span>
                            </div>
                            <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                              <div className="bg-indigo-600 h-full rounded-full transition-all duration-500" style={{ width: `${resumeHealth}%` }}></div>
                            </div>
                          </div>

                          {/* ATS Score */}
                          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/60 shadow-2xs">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-[10px] font-bold text-slate-550 uppercase tracking-wider">{t('resumeBuilder.aiReview.atsScore', 'ATS Score')}</span>
                              <span className="text-xs font-black text-emerald-600">{atsScore}%</span>
                            </div>
                            <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                              <div className="bg-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${atsScore}%` }}></div>
                            </div>
                          </div>

                          {/* Job Match */}
                          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/60 shadow-2xs">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-[10px] font-bold text-slate-550 uppercase tracking-wider">{t('resumeBuilder.aiReview.jobMatch', 'Job Match')}</span>
                              <span className="text-xs font-black text-sky-600">{jobMatchScore !== null ? `${jobMatchScore}%` : t('resumeBuilder.aiReview.notApplicable', 'N/A')}</span>
                            </div>
                            <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                              <div className="bg-sky-500 h-full rounded-full transition-all duration-500" style={{ width: `${jobMatchScore !== null ? jobMatchScore : 0}%` }}></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* AI Suggestions Section */}
                      <div className="space-y-2">
                        <span className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.aiReview.aiSuggestions', 'AI Suggestions')}</span>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-[11px] text-slate-650 font-bold bg-amber-50/50 p-2.5 rounded-xl border border-amber-100/60">
                            <div className="flex items-start gap-2">
                              <span className="text-amber-550 flex-shrink-0">⚠️</span>
                              <span>{t('resumeBuilder.aiReview.suggestions.quantifiedAchievements', 'Add more quantified achievements.')}</span>
                            </div>
                            <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-100/50 px-2 py-0.5 rounded-md whitespace-nowrap">+6 ATS</span>
                          </div>
                          <div className="flex items-center justify-between text-[11px] text-slate-650 font-bold bg-amber-50/50 p-2.5 rounded-xl border border-amber-100/60">
                            <div className="flex items-start gap-2">
                              <span className="text-amber-550 flex-shrink-0">⚠️</span>
                              <span>{t('resumeBuilder.aiReview.suggestions.keywordsJobDescription', 'Include more keywords from job description.')}</span>
                            </div>
                            <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-100/50 px-2 py-0.5 rounded-md whitespace-nowrap">+4 ATS</span>
                          </div>
                          <div className="flex items-center justify-between text-[11px] text-slate-650 font-bold bg-amber-50/50 p-2.5 rounded-xl border border-amber-100/60">
                            <div className="flex items-start gap-2">
                              <span className="text-amber-550 flex-shrink-0">⚠️</span>
                              <span>{t('resumeBuilder.aiReview.suggestions.certificationsCredibility', 'Add certifications to boost credibility.')}</span>
                            </div>
                            <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-100/50 px-2 py-0.5 rounded-md whitespace-nowrap">+2 ATS</span>
                          </div>
                        </div>
                      </div>

                      {/* AI Quick Fixes Grid */}
                      <div className="space-y-2">
                        <span className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.aiReview.aiQuickFixes', 'AI Quick Fix Actions')}</span>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setActiveStep(1);
                              handleAiImprove('summary', resumeData.personalInfo.summary || 'Generate summary.');
                            }}
                            className="flex items-center justify-center gap-1.5 p-2 rounded-xl bg-indigo-50 border border-indigo-150 hover:bg-indigo-100 transition-all text-left group cursor-pointer shadow-2xs"
                          >
                            <span className="text-indigo-600 font-bold text-xs group-hover:scale-110 transition-all">✨</span>
                            <span className="text-[11px] font-bold text-indigo-700">{t('resumeBuilder.aiReview.improveSummary', 'Improve Summary')}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveStep(3);
                              showAlert(t('resumeBuilder.alerts.aiExperienceImprovement', "Click an experience item in the form below and select 'Improve Description with AI' to refine your bullets."), t('resumeBuilder.alerts.aiExperienceImprovementTitle', "AI Experience Improvement"), "info");
                            }}
                            className="flex items-center justify-center gap-1.5 p-2 rounded-xl bg-sky-50 border border-sky-150 hover:bg-sky-100 transition-all text-left group cursor-pointer shadow-2xs"
                          >
                            <span className="text-sky-600 font-bold text-xs group-hover:scale-110 transition-all">💼</span>
                            <span className="text-[11px] font-bold text-sky-700">{t('resumeBuilder.aiReview.improveExperience', 'Improve Experience')}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveStep(5);
                            }}
                            className="flex items-center justify-center gap-1.5 p-2 rounded-xl bg-emerald-50 border border-emerald-150 hover:bg-emerald-100 transition-all text-left group cursor-pointer shadow-2xs"
                          >
                            <span className="text-emerald-600 font-bold text-xs group-hover:scale-110 transition-all">💡</span>
                            <span className="text-[11px] font-bold text-emerald-700">{t('resumeBuilder.aiReview.suggestSkills', 'Suggest Skills')}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveStep(7);
                            }}
                            className="flex items-center justify-center gap-1.5 p-2 rounded-xl bg-amber-50 border border-amber-150 hover:bg-amber-100 transition-all text-left group cursor-pointer shadow-2xs"
                          >
                            <span className="text-amber-600 font-bold text-xs group-hover:scale-110 transition-all">🏆</span>
                            <span className="text-[11px] font-bold text-amber-700">{t('resumeBuilder.aiReview.addAchievements', 'Add Achievements')}</span>
                          </button>
                        </div>
                      </div>

                      {/* Job Match Analysis */}
                      <div className="space-y-2">
                        <span className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('resumeBuilder.aiReview.jobMatchAnalysis', 'Job Match Analysis')}</span>
                        <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200/60 shadow-2xs space-y-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">{t('resumeBuilder.aiReview.targetJob', 'Target Job')}</label>
                            <PremiumSelect
                              variant="b2c"
                              size="sm"
                              value={selectedJobId}
                              onChange={setSelectedJobId}
                              options={publicJobs.map(job => ({ value: String(job.id), label: `${job.title} (${job.company_name || 'TechSolv'})` }))}
                              placeholder={t('resumeBuilder.aiReview.selectTargetJob', 'Select target job description...')}
                              searchable={publicJobs.length > 5}
                            />
                          </div>

                          <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-2.5">
                            {jobMatchScore !== null ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{t('resumeBuilder.aiReview.semanticMatch', 'Semantic Match:')}</span>
                                <span className={`text-xs font-black ${
                                  jobMatchScore >= 80 ? 'text-emerald-600' : jobMatchScore >= 60 ? 'text-sky-600' : 'text-amber-600'
                                }`}>{jobMatchScore}%</span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-slate-400 font-medium">{t('resumeBuilder.aiReview.selectJobToCalculate', 'Select a job to calculate score.')}</span>
                            )}
                            <button
                              type="button"
                              onClick={runJobMatch}
                              disabled={jobMatchLoading || !selectedJobId}
                              className="flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 disabled:opacity-50 cursor-pointer shadow-2xs"
                            >
                              {jobMatchLoading ? t('resumeBuilder.buttons.analyzing', 'Analyzing...') : t('resumeBuilder.buttons.calculateMatch', 'Calculate Match')}
                            </button>
                          </div>

                          {selectedJobId && (
                            <div className="border-t border-slate-100 pt-2.5 space-y-1.5">
                              <span className="text-[10px] font-bold text-slate-550 uppercase tracking-wider block">{t('resumeBuilder.aiReview.missingKeywords', 'Missing Keywords')}</span>
                              <div className="flex flex-wrap gap-1.5">
                                {['Spring Boot', 'Microservices', 'REST API', 'Docker'].map(kw => (
                                  <span key={kw} className="bg-rose-50 text-rose-600 border border-rose-100 rounded-lg px-2 py-0.5 text-[10px] font-bold">
                                    ❌ {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Accent Color picker (Retained as requested) */}
                      <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200/60 shadow-2xs">
                        <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">{t('resumeBuilder.aiReview.accentColor', 'Accent Color')}</label>
                        <div className="flex flex-wrap gap-2">
                          {themeColors.map(color => (
                            <button
                              key={color.hex}
                              type="button"
                              onClick={() => setResumeData(prev => ({ ...prev, themeColor: color.hex }))}
                              className="h-7 w-7 rounded-full shadow-sm hover:scale-110 active:scale-95 transition-all flex items-center justify-center relative border border-slate-200 cursor-pointer"
                              style={{ backgroundColor: color.hex }}
                              title={color.name}
                            >
                              {resumeData.themeColor === color.hex && (
                                <span className="h-4 w-4 bg-white rounded-full flex items-center justify-center shadow-xs">
                                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color.hex }}></span>
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Export Section Action buttons */}
                      <div className="space-y-2 pt-2 border-t border-slate-100">
                        <span className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">{t('resumeBuilder.aiReview.exportResume', 'Export Resume')}</span>
                        <button
                          type="button"
                          onClick={downloadPDF}
                          disabled={downloading}
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3 text-xs font-bold transition-all active:scale-95 shadow-md hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <DownloadIcon sx={{ fontSize: 18 }} />
                          {downloading ? t('resumeBuilder.buttons.downloading', 'Generating PDF...') : t('resumeBuilder.aiReview.downloadPdfDocument', 'Download PDF Document')}
                        </button>

                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={saveDraft}
                            disabled={saving}
                            className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl py-2.5 text-xs font-bold transition-all active:scale-95 shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                          >
                            <SaveIcon sx={{ fontSize: 16 }} />
                            {saving ? t('resumeBuilder.buttons.saving', 'Saving...') : t('resumeBuilder.buttons.saveDraft', 'Save Draft')}
                          </button>

                          <button
                            type="button"
                            onClick={() => setIsFullScreenOpen(true)}
                            className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl py-2.5 text-xs font-bold transition-all active:scale-95 shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4" />
                            </svg>
                            {t('resumeBuilder.buttons.fullScreenPreview', 'Full Screen Preview')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pagination Controls */}
                <div className="flex justify-between items-center border-t border-slate-100 pt-4 mt-6">
                  <button
                    onClick={() => setActiveStep(prev => Math.max(1, prev - 1))}
                    disabled={activeStep === 1}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-all active:scale-95 bg-white"
                  >
                    <ArrowBackIcon sx={{ fontSize: 16 }} />
                    {t('resumeBuilder.buttons.previous', 'Previous')}
                  </button>

                  <button
                    onClick={() => setActiveStep(prev => Math.min(8, prev + 1))}
                    disabled={activeStep === 8}
                    className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-40 transition-all active:scale-95 shadow-sm"
                  >
                    {t('resumeBuilder.buttons.next', 'Next')}
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              </div>
            </div>

            {/* Column 3: Live Preview (width lg:w-[45%]) */}
            <div className="w-full lg:w-[45%] flex flex-col justify-between flex-shrink-0">
              <div className="bg-white p-5 rounded-xl border border-slate-200/80 shadow-sm flex-grow flex flex-col justify-between">
                <div>
                  {/* Preview Header & Controls */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3 mb-4">
                    <div className="flex flex-col gap-1.5">
                      <h2 className="text-base font-extrabold text-slate-800 tracking-tight">{t('resumeBuilder.preview.livePreview', 'Live Preview')}</h2>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => setIsTemplateModalOpen(true)}
                          className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50/50 hover:bg-indigo-50 px-3 py-1.5 rounded-lg border border-indigo-100/50 hover:border-indigo-200/80 transition-all active:scale-95 cursor-pointer shadow-xs"
                          title="Open Template Gallery"
                        >
                          <span>{t('resumeBuilder.buttons.changeTemplate', 'Change Template')}</span>
                        </button>
                        <span className="text-xs text-slate-500 font-medium">
                          {t('resumeBuilder.preview.current', 'Current')}: <strong className="text-slate-700">{t('resumeBuilder.templates.' + resumeData.template + '.name', templates.find(t => t.id === resumeData.template)?.name || 'Modern Professional')}</strong>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-0.5">
                      <button
                        onClick={() => setDeviceView('desktop')}
                        className={`flex items-center gap-1 px-2 py-1.5 rounded text-[11px] font-semibold transition-all ${
                          deviceView === 'desktop'
                            ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/50'
                            : 'text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        <DesktopIcon sx={{ fontSize: 12 }} />
                        <span>{t('resumeBuilder.buttons.desktop', 'Desktop')}</span>
                      </button>
                      <button
                        onClick={() => setDeviceView('a4')}
                        className={`flex items-center gap-1 px-2 py-1.5 rounded text-[11px] font-semibold transition-all ${
                          deviceView === 'a4'
                            ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/50'
                            : 'text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        <DescriptionIcon sx={{ fontSize: 12 }} />
                        <span>{t('resumeBuilder.preview.a4', 'A4')}</span>
                      </button>
                      <button
                        onClick={() => setIsFullScreenOpen(true)}
                        className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-white"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Scrollable Preview Frame */}
                  <div className="flex-grow overflow-auto flex justify-center bg-slate-100 border border-slate-200 rounded-xl p-3 max-h-[600px] min-h-[500px]">
                    <div
                      className="bg-white border border-slate-200 shadow-lg text-left select-none relative origin-top scale-90 md:scale-95 xl:scale-100 transition-all duration-300"
                      style={{
                        width: deviceView === 'desktop' ? '100%' : deviceView === 'a4' ? '210mm' : deviceView === 'tablet' ? '460px' : '310px',
                        minHeight: '297mm',
                        fontSize: deviceView === 'desktop' ? '12px' : deviceView === 'a4' ? '11px' : deviceView === 'tablet' ? '10px' : '8px'
                      }}
                    >
                      {renderActiveTemplate(false)}
                    </div>
                  </div>
                </div>

                {/* Bottom Actions under preview */}
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100">
                  <button
                    onClick={() => navigate('/resume-analyze')}
                    className="rounded-lg border border-slate-200 bg-white py-2.5 text-center text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-all active:scale-95 shadow-sm"
                  >
                    {t('resumeBuilder.buttons.reAnalyze', 'Re-analyze Resume')}
                  </button>

                  <button
                    onClick={() => showAlert(t('resumeBuilder.alerts.atsDetailedAnalysis', 'ATS Detailed Analysis: Dynamic Score is {{score}}/100. {{grade}}', { score: atsScore, grade: atsGrade.label }), t('resumeBuilder.alerts.atsDetailedAnalysisTitle', "ATS Detailed Analysis"), "info")}
                    className="rounded-lg border border-slate-200 bg-white py-2.5 text-center text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-all active:scale-95 shadow-sm"
                  >
                    {t('resumeBuilder.buttons.atsAnalysis', 'Analyze ATS')}
                  </button>

                  <button
                    onClick={runJobMatch}
                    disabled={jobMatchLoading || !selectedJobId}
                    className="rounded-lg bg-indigo-600 py-2.5 text-center text-xs font-bold text-white hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50 shadow-sm"
                  >
                    {jobMatchLoading ? t('resumeBuilder.buttons.analyzing', 'Analyzing...') : t('resumeBuilder.aiReview.jobMatch', 'Job Match')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Full Screen Preview Modal */}
      {/* Full Screen Preview Modal */}
      {isFullScreenOpen && createPortal(
        <div className="fixed inset-0 z-[9999] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-100 rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col md:flex-row h-[90vh] overflow-hidden border border-slate-200 animate-fadeIn">
            {/* Resume A4 Canvas (Left 70% area) */}
            <div className="flex-1 overflow-auto bg-slate-500/10 p-6 flex justify-center items-start">
              <div
                className="bg-white border border-slate-200 shadow-xl text-left select-none relative origin-top scale-95 md:scale-100 transition-all duration-300 my-4"
                style={{
                  width: (deviceView === 'desktop' || deviceView === 'a4') ? '820px' : deviceView === 'tablet' ? '460px' : '310px',
                  minHeight: '297mm',
                  fontSize: (deviceView === 'desktop' || deviceView === 'a4') ? '12px' : deviceView === 'tablet' ? '10px' : '8px'
                }}
              >
                {renderActiveTemplate(true)}
              </div>
            </div>

            {/* Control panel (Right 30% area) */}
            <div className="w-full md:w-80 bg-white border-t md:border-t-0 md:border-l border-slate-200 p-5 flex flex-col justify-between overflow-y-auto">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                    <span>🎛️</span> {t('resumeBuilder.preview.customizePreview', 'Customize Preview')}
                  </h3>
                  <button
                    onClick={() => setIsFullScreenOpen(false)}
                    className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>



                {/* Theme Color Picker */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">{t('resumeBuilder.aiReview.accentColor', 'Accent Color')}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {themeColors.map(color => (
                      <button
                        key={color.hex}
                        onClick={() => setResumeData(prev => ({ ...prev, themeColor: color.hex }))}
                        className="h-8 rounded-lg text-[10px] font-bold text-white shadow-sm transition-all active:scale-95 flex items-center justify-center relative"
                        style={{ backgroundColor: color.hex }}
                      >
                        {color.name}
                        {resumeData.themeColor === color.hex && (
                          <span className="absolute -top-1 -right-1 h-3.5 w-3.5 bg-black rounded-full text-white flex items-center justify-center text-[7px] border border-white">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Device toggle */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">{t('resumeBuilder.preview.deviceView', 'Device View')}</label>
                  <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1">
                    <button
                      onClick={() => setDeviceView('desktop')}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-xs font-bold ${
                        (deviceView === 'desktop' || deviceView === 'a4') ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <DesktopIcon sx={{ fontSize: 14 }} /> {t('resumeBuilder.buttons.desktop', 'Desktop')}
                    </button>
                    <button
                      onClick={() => setDeviceView('tablet')}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-xs font-bold ${
                        deviceView === 'tablet' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <TabletIcon sx={{ fontSize: 14 }} /> {t('resumeBuilder.buttons.tablet', 'Tablet')}
                    </button>
                    <button
                      onClick={() => setDeviceView('mobile')}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-xs font-bold ${
                        deviceView === 'mobile' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <PhoneIcon sx={{ fontSize: 14 }} /> {t('resumeBuilder.buttons.mobile', 'Mobile')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-slate-100 space-y-2 mt-6">
                <div className="text-[11px] font-bold text-center text-slate-400 mb-1 flex items-center justify-center gap-1">
                  <span>●</span> {getSaveStatusLabel(saveStatus)}
                </div>
                <button
                  onClick={saveDraft}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 disabled:opacity-50 transition-all"
                >
                  <SaveIcon sx={{ fontSize: 16 }} />
                  {saving ? t('resumeBuilder.buttons.saving', 'Saving...') : t('resumeBuilder.buttons.saveDraft', 'Save Draft')}
                </button>
                <button
                  onClick={downloadPDF}
                  disabled={downloading}
                  className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 py-2.5 text-xs font-bold text-white hover:bg-indigo-700 active:scale-95 disabled:opacity-50 transition-all shadow-sm"
                >
                  <DownloadIcon sx={{ fontSize: 16 }} />
                  {downloading ? t('resumeBuilder.buttons.downloading', 'Downloading...') : t('resumeBuilder.buttons.downloadPdf', 'Download PDF')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Custom Alert Dialog */}
      {/* Change Template Modal */}
      {isTemplateModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] bg-slate-900/85 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-100 rounded-2xl w-full max-w-[1360px] shadow-2xl flex flex-col md:flex-row h-[90vh] overflow-hidden border border-slate-200 animate-fadeIn">
            
            {/* Left Column: Live Scaled-down Preview */}
            <div className="w-full md:w-[60%] overflow-hidden bg-slate-500/10 p-6 flex flex-col justify-start items-center relative">
              <div className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">{t('resumeBuilder.preview.livePreview', 'Live Preview')}</div>
              <div className="w-full flex-grow overflow-auto flex justify-center items-start">
                <div
                  className="bg-white border border-slate-200 shadow-xl text-left select-none relative origin-top transition-all duration-300 my-2"
                  style={{
                    width: '794px', // Standard A4 width proportion
                    minHeight: '297mm',
                    fontSize: '11px',
                    transform: 'scale(0.85)',
                    transformOrigin: 'top center'
                  }}
                >
                  {renderActiveTemplate(false, previewTemplateId || resumeData.template, { ...DEMO_RESUME_DATA, themeColor: resumeData.themeColor })}
                </div>
              </div>
            </div>

            {/* Right Column: Template Choices & Accent Color */}
            <div className="w-full md:w-[40%] bg-white border-t md:border-t-0 md:border-l border-slate-200 flex flex-col justify-between overflow-hidden">
              
              {/* Top Header & Color Picker (Fixed) */}
              <div className="p-6 pb-4 border-b border-slate-100 flex-shrink-0 flex flex-col gap-4">
                {/* Header */}
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                    <span>🎨</span> {t('resumeBuilder.buttons.changeTemplate', 'Change Template')}
                  </h3>
                  <button
                    onClick={() => setIsTemplateModalOpen(false)}
                    className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>

                {/* Accent Color Swatches */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">{t('resumeBuilder.preview.chooseColor', 'Choose Color:')}</label>
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Preset colors */}
                    {[
                      { name: 'Indigo', hex: '#4f46e5' },
                      { name: 'Slate Blue', hex: '#0ea5e9' },
                      { name: 'Teal', hex: '#0d9488' },
                      { name: 'Green', hex: '#10b981' },
                      { name: 'Emerald', hex: '#16a34a' },
                      { name: 'Purple', hex: '#8b5cf6' },
                      { name: 'Orange', hex: '#ea580c' },
                      { name: 'Rose', hex: '#e11d48' },
                      { name: 'Slate Gray', hex: '#1e293b' }
                    ].map(color => (
                      <button
                        key={color.hex}
                        onClick={() => setResumeData(prev => ({ ...prev, themeColor: color.hex }))}
                        className="h-7 w-7 rounded-full shadow-sm hover:scale-110 active:scale-95 transition-all flex items-center justify-center relative border border-slate-200 cursor-pointer"
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      >
                        {resumeData.themeColor === color.hex && (
                          <span className="h-4 w-4 bg-white rounded-full flex items-center justify-center shadow-sm">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color.hex }}></span>
                          </span>
                        )}
                      </button>
                    ))}

                    {/* Custom Hex Color Picker (Zety plus button!) */}
                    <div className="relative h-7 w-7 rounded-full border border-slate-200 shadow-sm hover:scale-110 active:scale-95 transition-all flex items-center justify-center bg-slate-50 overflow-hidden cursor-pointer group">
                      <span className="text-slate-400 font-bold text-sm group-hover:text-indigo-600 transition-colors">+</span>
                      <input
                        type="color"
                        value={resumeData.themeColor}
                        onChange={e => setResumeData(prev => ({ ...prev, themeColor: e.target.value }))}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        title="Choose Custom Color"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Layout Picker (Scrollable Light premium SaaS theme) */}
              <div className="bg-[#f8fafc] p-6 flex-grow flex flex-col justify-between overflow-y-auto">
                <div className="space-y-6">
                  {/* Category Filters */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">{t('resumeBuilder.preview.categoryFilter', 'Category Filter:')}</label>
                    <div className="flex flex-wrap gap-1.5">
                      {['All', 'Professional', 'Creative', 'Academic'].map(cat => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setFilterCategory(cat)}
                          className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all border cursor-pointer ${
                            filterCategory === cat
                              ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-800'
                          }`}
                        >
                          {t('resumeBuilder.categories.' + cat.toLowerCase(), cat)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Templates Grid Picker */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">{t('resumeBuilder.preview.chooseLayout', 'Choose Layout:')}</label>
                    <div className="grid grid-cols-2 gap-4 pr-1">
                      {templates
                        .filter(tpl => filterCategory === 'All' || tpl.category === filterCategory)
                        .map(tpl => (
                          <button
                            key={tpl.id}
                            type="button"
                            onMouseEnter={() => setPreviewTemplateId(tpl.id)}
                            onClick={() => {
                              setResumeData(prev => ({ ...prev, template: tpl.id }));
                              setPreviewTemplateId(tpl.id);
                            }}
                            className={`group rounded-2xl text-left transition-all flex flex-col cursor-pointer relative overflow-hidden h-[240px] shadow-sm hover:shadow-md ${
                              resumeData.template === tpl.id
                                ? 'border-[3px] border-purple-500 ring-4 ring-purple-500/20 bg-purple-50/10 shadow-[0_0_15px_rgba(168,85,247,0.15)] scale-[1.01]'
                                : 'border-[3px] border-slate-200/80 bg-white hover:border-slate-350 hover:bg-slate-50/40 hover:shadow-xs'
                            }`}
                          >
                            {/* Visual Mock Thumbnail (Large top section) */}
                            <div className="w-full h-[155px] bg-slate-50 border-b border-slate-100 flex items-center justify-center overflow-hidden p-1.5 relative">
                              <div className="w-[127px] h-[143px] overflow-hidden flex justify-center items-start bg-white border border-slate-200/60 rounded-md shadow-2xs relative">
                                <div className="w-[794px] h-[1122.5px] bg-white text-left select-none relative origin-top pointer-events-none" style={{ transform: 'scale(0.16)', transformOrigin: 'top center' }}>
                                  {renderActiveTemplate(false, tpl.id, { ...DEMO_RESUME_DATA, themeColor: resumeData.themeColor })}
                                </div>
                              </div>
                              
                              {/* Selected Badge */}
                              {resumeData.template === tpl.id && (
                                <div className="absolute top-2.5 right-2.5 bg-purple-600 text-white text-[8px] font-black px-2 py-0.5 rounded-md shadow-sm z-20 flex items-center gap-1 uppercase tracking-wider animate-fadeIn">
                                  <span>✓</span> {t('resumeBuilder.buttons.selected', 'Selected')}
                                </div>
                              )}

                              {/* Hover Overlay Button */}
                              <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-xs duration-200">
                                <span className="bg-white text-indigo-650 text-[10px] font-black px-3 py-1.5 rounded-lg shadow-md uppercase tracking-wider scale-90 group-hover:scale-100 transition-transform duration-200">
                                  {t('resumeBuilder.buttons.useLayout', '✓ Use Layout')}
                                </span>
                              </div>

                              {/* Recommended Ribbon Overlay */}
                              {tpl.badge && (
                                <div className="absolute top-2.5 left-2.5 bg-indigo-650 text-white text-[7px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider shadow-sm scale-90 origin-top-left group-hover:scale-0 transition-transform duration-150">
                                  {t('resumeBuilder.badges.' + tpl.badge.toLowerCase(), tpl.badge)}
                                </div>
                              )}
                            </div>

                            {/* Details Stack (Bottom section) */}
                            <div className="p-2.5 flex-1 w-full flex flex-col justify-between">
                              <div className="flex items-center justify-between gap-1 w-full">
                                <span className="text-[11px] font-extrabold text-slate-800 leading-none truncate" title={tpl.name}>
                                  {t('resumeBuilder.templates.' + tpl.id + '.name', tpl.name)}
                                </span>
                                <span className="text-[8px] text-slate-400 font-bold flex-shrink-0">
                                  {t('resumeBuilder.categories.' + tpl.category.toLowerCase(), tpl.category)}
                                </span>
                              </div>
                              
                              {/* ATS Score Indicator */}
                              <div className="flex items-center justify-between mt-1 w-full">
                                <span className={`text-[8px] font-extrabold px-1.5 py-0.5 rounded-md uppercase tracking-wider flex items-center gap-0.5 ${
                                  tpl.atsScore >= 95 
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                                    : tpl.atsScore >= 90
                                      ? 'bg-teal-50 text-teal-700 border border-teal-100'
                                      : 'bg-blue-50 text-blue-700 border border-blue-100'
                                }`}>
                                  📈 {t('resumeBuilder.aiReview.atsScore', 'ATS Score')}: {tpl.atsScore}%
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Apply/Close */}
                <div className="pt-4 border-t border-slate-200/70 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsTemplateModalOpen(false)}
                    className="w-full bg-indigo-600 text-white rounded-xl py-2.5 text-xs font-bold hover:bg-indigo-700 active:scale-95 transition-all shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                    {t('resumeBuilder.buttons.applyClose', 'Apply & Close')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {alertDialog.isOpen && createPortal(
        <div className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl border border-slate-200/80 p-6 animate-fadeIn">
            {/* Title & Close */}
            <div className="flex justify-between items-center pb-3 border-b border-slate-100 mb-4">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                {alertDialog.type === 'error' && <span className="text-rose-500">❌</span>}
                {alertDialog.type === 'warning' && <span className="text-amber-500">⚠️</span>}
                {alertDialog.type === 'success' && <span className="text-emerald-500">✅</span>}
                {alertDialog.type === 'info' && <span className="text-indigo-500">ℹ️</span>}
                {alertDialog.title}
              </h3>
              <button
                onClick={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
                className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>

            {/* Message Body */}
            <p className="text-xs font-bold text-slate-600 leading-relaxed mb-6">
              {alertDialog.message}
            </p>

            {/* Actions */}
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
                className={`px-6 py-2.5 rounded-lg text-xs font-bold text-white transition-all active:scale-95 shadow-sm ${
                  alertDialog.type === 'error' || alertDialog.type === 'warning'
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {t('resumeBuilder.buttons.ok', 'OK')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      </PageCard>
    </PageShell>
  );
}
